import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

import OtpVerification from "./OtpVerification";
import CustomerDetails from "./CustomerDetails";
import PetDetails, { PetTabs } from "./PetDetails";
import {
  applyCustomerMapsTo,
  applyPetMapsTo,
  finalizeMappedPet,
} from "./formFieldMap";
import config from "../../../config";

/* ---------- CONFIG ---------- */
const API_URL =
  config.API_BASE_URL || "http://localhost:5002/api/v1.0";
const api = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});
const OTP_TYPE = "register";
const OTP_APP_TYPE = "customer";
const HIDDEN_PATTERNS = [/customer\s*ref/i];
const MAX_PETS = 10;
const MAX_FILE_MB = 5;

/* ---------- DOC TYPE MAP ---------- */
const docTypeFor = (qText) => {
  const t = (qText || "").toLowerCase();
  if (/vaccin/.test(t)) return "vaccination";
  if (/prescription/.test(t)) return "prescription";
  if (/lab/.test(t)) return "lab";
  if (/insurance/.test(t)) return "insurance";
  if (/medical/.test(t)) return "medical";
  if (/pet\s*profile|pet.*photo|profile\s*pic/.test(t)) return "pet_photo";
  return "document";
};

/* ---------- API ---------- */
const sendOtp = (phone) =>
  api
    .post("/user/sendOTP", { phone, type: OTP_TYPE, appType: OTP_APP_TYPE })
    .then((r) => r.data);

const verifyOtp = (phone, otp) =>
  api
    .post("/user/verifyOTP", {
      phone,
      otp,
      type: OTP_TYPE,
      appType: OTP_APP_TYPE,
    })
    .then((r) => r.data);

const getVendorProfile = (vendorId) =>
  api
    .get("/publicuser/getPublicProfile", { params: { vendorId } })
    .then((r) => r.data.data);

const getFormById = (vendorId, formId) =>
  api
    .get(`/consentForm/getFormById/${vendorId}/${formId}`)
    .then((r) => r.data.data);

// const getPreviousSubmission = (vendorId, phone, formId) =>
//   api
//     .get(`/publicclientforms/previous/${vendorId}`, {
//       params: { phone, form_id: formId },
//     })
//     .then((r) => r.data.data)
//     .catch(() => ({ found: false }));

const submitForm = (vendorId, payload) =>
  api
    .post(`/publicclientforms/submitform/${vendorId}`, payload)
    .then((r) => r.data);

const getExistingSubmission = (vendorId, formId, phone) =>
  api
    .get(`/publicclientforms/previous/${vendorId}`, {
      params: { phone, form_id: formId },
    })
    .then((r) => (r.data?.data?.found ? r.data.data : null))
    .catch(() => null);

/** Upload ONE file straight to S3 via backend. Returns {name,url,key,type,size,category}. */
const uploadPublicFile = async (vendorId, file, category) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  const { data } = await api.post(`/publicclientforms/upload/${vendorId}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  const d = data?.data;
  if (!d?.key || !d?.url) throw new Error("Upload failed");
  return d;
};

const safeList = (v) =>
  !v
    ? []
    : Array.isArray(v)
      ? v
      : Array.isArray(v.data)
        ? v.data
        : Array.isArray(v.result)
          ? v.result
          : [];

const getPetTypes = (vendorId) =>
  api
    .get("/customer/customerpettype", { params: { vendor_id: vendorId } })
    .then((r) => safeList(r.data))
    .catch(() => []);

const getPetBreeds = (petTypeId) =>
  !petTypeId
    ? []
    : api
        .get("/customer/getPetBreeds/", { params: { pet_type: petTypeId } })
        .then((r) => safeList(r.data))
        .catch(() => []);

const getPetSizes = (vendorId, petTypeId) =>
  !petTypeId
    ? []
    : api
        .get("/customer/getPetsizes/", {
          params: { vendor_id: vendorId, pet_type: petTypeId },
        })
        .then((r) => safeList(r.data))
        .catch(() => []);

/* ---------- CONSTANTS ---------- */
export const TEXTY = [
  "short_text",
  "text",
  "phone",
  "number",
  "description_text",
];
export const NO_ANSWER = ["section_heading"];
export const MAX_PETS_CONST = MAX_PETS;
export const MAX_FILE_MB_CONST = MAX_FILE_MB;

/* ---------- QUESTION / SECTION PREDICATES ---------- */
export const qText = (q) => (q?.question || "").toLowerCase();
export const isWhatsappQ = (q) => /whats\s*app/.test(qText(q));
export const isLockedPhoneQ = (q) => {
  const t = qText(q);
  return (
    /(phone|mobile)/.test(t) &&
    !/(whats|alt|emergency|other|secondary|vet|doctor)/.test(t) &&
    TEXTY.includes((q.answerType || "").toLowerCase())
  );
};
export const isPetTypeQ = (q) => /pet\s*type/.test(qText(q));
export const isBreedQ = (q) => /breed/.test(qText(q));
export const isSizeQ = (q) => /\bsize\b/.test(qText(q));
export const isGenderQ = (q) => /\b(gender|sex)\b/.test(qText(q));
export const isMealTypeQ = (q) => /meal\s*type/.test(qText(q));
export const isMealsPerDayQ = (q) => /meal[s]?\s*per\s*day/.test(qText(q));
export const isDobQ = (q) =>
  /\b(dob|date\s*of\s*birth|birth\s*date)\b/.test(qText(q));
export const isAgeQ = (q) => !isDobQ(q) && /\bage\b/.test(qText(q));
export const isPetNameQ = (q) => /pet\s*name/.test(qText(q));

export const isMealSectionHeading = (q) =>
  q.answerType === "section_heading" && /meal/i.test(qText(q));
export const isMedSectionHeading = (q) =>
  q.answerType === "section_heading" && /medication/i.test(qText(q));
export const isDietTypeQ = (q) => /diet\s*type/.test(qText(q));

export const isServiceInterestQ = (q) => /services?\s*interest/i.test(qText(q));
export const isHealthSection = (q) =>
  q.answerType === "section_heading" &&
  /health\s*and\s*medical/i.test(qText(q));
export const isVaccinationSection = (q) =>
  q.answerType === "section_heading" && /vaccination/i.test(qText(q));
export const isAppearanceSection = (q) =>
  q.answerType === "section_heading" && /app?e?a?r[ae]n[cs]e/i.test(qText(q));
export const isBehaviourSection = (q) =>
  q.answerType === "section_heading" && /behaviou?r/i.test(qText(q));
export const isDietSection = (q) =>
  q.answerType === "section_heading" &&
  /diet\s*and\s*meal|meal\s*instruction/i.test(qText(q));
export const isConsentSection = (q) =>
  q.answerType === "section_heading" &&
  /consent|declaration|boarding\s*terms|owner\s*declaration/i.test(qText(q));
export const isMedicalYNQ = (q) =>
  /medical\s*condition/i.test(qText(q)) &&
  ["yes_no", "single_choice", "radio", "dropdown"].includes(
    (q.answerType || "").toLowerCase(),
  ) &&
  !isCustomMedCondQ(q);
export const isMedicalConditionsQ = (q) =>
  /^medical\s*conditions$/i.test((q.question || "").trim());
export const isCustomMedCondQ = (q) => /custom\s*medical/i.test(qText(q));
export const isOnMedicationQ = (q) => {
  const t = qText(q);
  if (q.answerType === "section_heading") return false;
  // Only treat "Yes/No-ish" inputs as the gate
  const gateTypes = ["single_choice", "yes_no", "dropdown", "radio"];
  if (!gateTypes.includes((q.answerType || "").toLowerCase())) return false;
  // Must mention "medication" or "medications"
  if (!/medications?/.test(t)) return false;
  // Reject the actual detail fields that live inside the block
  if (
    /name|dosage|time|instruction|photo|how\s*much|when\s*to\s*give|given\s*on|notes/i.test(
      t,
    )
  )
    return false;
  return true;
};
export const isWhatToFeedQ = (q) => /what\s*to\s*feed/i.test(qText(q));
export const isWhenToFeedQ = (q) => /when\s*to\s*feed/i.test(qText(q));
export const isHowMuchToFeedQ = (q) => /how\s*much\s*to\s*feed/i.test(qText(q));
export const isFeedingInstructionsQ = (q) =>
  /feeding\s*instructions/i.test(qText(q));

export const isShortQ = (q) => {
  if (
    isMealTypeQ(q) ||
    isMealsPerDayQ(q) ||
    isPetTypeQ(q) ||
    isBreedQ(q) ||
    isSizeQ(q)
  )
    return false;
  if (isDobQ(q) || isAgeQ(q)) return true;
  return /\b(weight|qty|quantity|age|gender|sex|units?|dob)\b/.test(qText(q));
};

/* ---------- VALIDATION / NORMALIZATION ---------- */
export const validatePhone = (n) => {
  const d = n.replace(/\D/g, "");
  if (!d) return "Mobile number is required";
  if (d.length !== 10) return "Enter a valid 10-digit mobile number";
  return null;
};
export const normalizeOptions = (q) => {
  const asStr = (x) => {
    if (x == null) return "";
    if (typeof x === "string" || typeof x === "number") return String(x);
    return String(
      x.en ?? x.text ?? x.value ?? x.name ?? x.label ?? JSON.stringify(x),
    );
  };
  return (q.options || []).map((o) => {
    if (typeof o === "string") return { label: o, value: o };
    if (o && typeof o === "object") {
      return {
        label: asStr(o.optionText ?? o.label ?? o.name ?? o.value),
        value: asStr(o.optionText ?? o.label ?? o.name ?? o.value),
      };
    }
    return { label: String(o), value: String(o) };
  });
};
/* Accepts "yes" / "Yes" / " Yes " / option _id whose label is "Yes" */
export const looksYes = (val, q) => {
  if (val == null) return false;
  if (/^yes$/i.test(String(val).trim())) return true;
  const opts = normalizeOptions(q);
  const m = opts.find((o) => String(o.value) === String(val));
  return !!m && /^yes$/i.test(String(m.label).trim());
};
export const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .trim();

/* Uploaded-file descriptor looks like { name, url, key, type, size, category } */
export const isFileVal = (x) =>
  !!x &&
  typeof x === "object" &&
  !Array.isArray(x) &&
  "url" in x &&
  "name" in x;
export const isFileLike = (v) =>
  isFileVal(v) || (Array.isArray(v) && v.length > 0 && v.every(isFileVal));
export const asList = (v) => (Array.isArray(v) ? v : v ? [v] : []);
export const toDocs = (v, q, category) =>
  asList(v)
    .filter(isFileVal)
    .map((f) => ({
      ...f,
      name: `${q.question}: ${f.name}`,
      category: f.category || category,
    }));

export const isEmpty = (v) =>
  v === undefined ||
  v === null ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);
export const petKey = (qid, idx) => `${qid}__${idx}`;

export const formatAge = (y, m) => {
  const yy = parseInt(y, 10) || 0;
  const mm = parseInt(m, 10) || 0;
  const parts = [];
  if (yy > 0) parts.push(`${yy} ${yy === 1 ? "year" : "years"}`);
  if (mm > 0) parts.push(`${mm} ${mm === 1 ? "month" : "months"}`);
  return parts.join(" ");
};

export const parseAge = (s) => {
  const str = String(s || "").trim();
  if (!str) return { years: "", months: "" };
  if (/^\d+$/.test(str)) return { years: str, months: "" }; // old data: "3"
  const y = str.match(/(\d+)\s*y/i);
  const m = str.match(/(\d+)\s*m/i);
  return { years: y ? y[1] : "", months: m ? m[1] : "" };
};

const calcAge = (dobStr) => {
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  if (dob > today) return null;
  let years = today.getFullYear() - dob.getFullYear();
  let months = today.getMonth() - dob.getMonth();
  if (today.getDate() < dob.getDate()) months--;
  if (months < 0) {
    years--;
    months += 12;
  }
  if (years < 0 || years > 120) return null;
  return formatAge(years, months) || "0 months";
};

const arrangeDobAge = (qs) => {
  const dob = qs.find(isDobQ);
  const age = qs.find(isAgeQ);
  if (!dob || !age || dob._id === age._id) return qs;
  const out = [];
  qs.forEach((q) => {
    if (q._id === age._id) return;
    out.push(q);
    if (q._id === dob._id) out.push(age);
  });
  return out;
};

// pet question text → backend key for file collections
const PET_FILE_KEYS = [
  [/vaccination\s*(record|certificate|proof)|vaccin/, "vaccination_record"],
  [/medical\s*record|medical/, "medical_record"],
  [/prescription/, "prescription"],
  [/lab\s*report|\blab\b/, "lab_report"],
  [/insurance/, "insurance_document"],
];

/* Merge records of the same vaccine: date + certificate end up in ONE record.
   Same vaccine with a different date stays separate (e.g. a booster). */
const mergeVaccRecords = (list) => {
  const out = [];
  for (const r of list || []) {
    if (!r) continue;
    const name = norm(r.vaccine);
    const date = r.date_given ? String(r.date_given).slice(0, 10) : "";
    const docs = Array.isArray(r.documents) ? r.documents : [];
    const target = out.find(
      (o) => norm(o.vaccine) === name && (!date || !o._d || o._d === date),
    );
    if (!target) {
      out.push({ ...r, date_given: r.date_given || "", documents: [...docs], _d: date });
      continue;
    }
    if (!target._d && date) {
      target._d = date;
      target.date_given = r.date_given;
    }
    for (const d of docs) {
      if (!target.documents.some((x) => (x.key || x.url) === (d.key || d.url)))
        target.documents.push(d);
    }
  }
  return out.map(({ _d, ...rest }) => rest);
};
/* ---------- SUBMIT PAYLOAD (uses S3 URLs, not base64) ---------- */
const buildSubmitPayload = ({
  customerQuestions,
  petQuestions,
  consentQ,
  answers,
  petCount,
  phone,
  petTypes,
  petRef,
  consent,
  schema,
  createAccount,
  serviceCategories,
}) => {
  const labelFor = (kind, value, idx) => {
    if (value === undefined || value === null || value === "") return "";
    const v = String(value);
    if (kind === "pet_type") {
      const t = (petTypes || []).find(
        (x) => String(x._id || x.id || x.flps_pet_type_id || x.name) === v,
      );
      return t
        ? t.name || t.pet_type || t.petType || t.pet_type_name || t.title || v
        : v;
    }
    const r = (petRef || {})[idx] || {};
    if (kind === "breed") {
      const b = (r.breeds || []).find(
        (x) => String(x._id || x.id || x.flps_pet_breed_id || x.name) === v,
      );
      return b
        ? b.breed_name || b.name || b.breed || b.title || b.label || v
        : v;
    }
    if (kind === "size") {
      const s = (r.sizes || []).find(
        (x) =>
          String(
            x.flps_pet_size_id || x._id || x.id || x.size_label || x.name,
          ) === v,
      );
      return s
        ? s.size_label ||
            s.name ||
            s.size ||
            s.size_name ||
            s.pet_size ||
            s.title ||
            v
        : v;
    }
    return v;
  };
  /* multiple_choice answers arrive as arrays; some backend fields are String.
     Join with ", " so the value fits the schema. */
  const asStr = (v) =>
    Array.isArray(v)
      ? v.join(", ")
      : v === undefined || v === null
        ? ""
        : String(v);

  const customer = {
    firstname: "",
    lastname: "",
    phone: phone || "",
    whatsapp_number: "",
    email: "",
    address: {
      address1: "",
      address2: "",
      landmark: "",
      city: "",
      state: "",
      zip: "",
    },
    reference_source: "",
    services_interested_in: [],
    notes: "",
    documents: [],
    signature: null,
    custom: {},
  };

  for (const q of customerQuestions) {
    if (q.answerType === "section_heading") continue;
    const t = qText(q);
    const type = (q.answerType || "").toLowerCase();
    const raw = answers[q._id];
    const v = raw; // already uploaded (S3 URLs) or plain value

    if (
      /whats\s*app/.test(t) &&
      TEXTY.includes((q.answerType || "").toLowerCase())
    ) {
      customer.whatsapp_number = raw || "";
      continue;
    }
    if (
      /\bphone\b|\bmobile\b/.test(t) &&
      !/whats|alt|emergency|other|secondary|vet|doctor/i.test(t)
    ) {
      customer.phone = raw || phone || "";
      continue;
    }
    if (v === undefined || v === null || v === "") continue;

    if (type === "signature") {
      customer.signature = typeof v === "string" ? v : null;
      continue;
    }

    if (isFileLike(v)) {
      customer.documents.push(...toDocs(v, q, "document"));
      continue;
    }

    if (applyCustomerMapsTo(customer, q, v, { isFileLike, toDocs })) {
      continue;
    }

    if (/\bfirst\s*name\b/.test(t)) customer.firstname = v;
    else if (/\blast\s*name\b/.test(t)) customer.lastname = v;
    else if (/\bemail\b/.test(t)) customer.email = v;
    else if (/address\s*(line\s*)?1|home\s*address/.test(t))
      customer.address.address1 = v;
    else if (/address\s*(line\s*)?2/.test(t)) customer.address.address2 = v;
    else if (/landmark/.test(t)) customer.address.landmark = v;
    else if (/\bcity\b/.test(t)) customer.address.city = v;
    else if (/\bstate\b/.test(t)) customer.address.state = v;
    else if (/pin\s*code|pincode|\bzip\b/.test(t))
      customer.address.zip = String(v);
    else if (/customer\s*ref|referred\s*by|reference/.test(t))
      customer.reference_source = v;
    else if (/services?\s*interest/.test(t))
      customer.services_interested_in = Array.isArray(v) ? v : [v];
    else if (/\bnotes?\b|comments?/.test(t)) customer.notes = v;
    else customer.custom[q._id || q.question] = { label: q.question, value: v };
  }

  const pets = Array.from({ length: Math.max(1, petCount) }, () => ({
    name: "",
    pet_type: "",
    breed: "",
    size: "",
    gender: "",
    age: "",
    birthday: null,
    weight_kg: null,
    color: [],
    hair_type: "",
    hair_length: "",
    neutered_status: "",
    vaccination_status: "",
    vaccinations: [],
    vaccination_records: [],
    medical_conditions: [],
    allergies: [],
    ongoing_medications: [],
    meal_type: [],
    meals_per_day: "",
    walks_per_day: "",
    behavior_notes: "",
    special_care_instructions: "",
    veterinarian: { name: "", clinic: "", phone: "" },
    image: null,
    photo: null,
    documents: [],
    meals: [],
    signature: null,
    custom: {},
  }));

  for (const q of petQuestions) {
    if (q.answerType === "section_heading") continue;
    const t = qText(q);
    const type = (q.answerType || "").toLowerCase();
    for (let i = 0; i < petCount; i++) {
      const key = petKey(q._id, i);
      const raw = answers[key];
      const v = raw;
      if (v === undefined || v === null || v === "") continue;
      const pet = pets[i];

      if (type === "signature") {
        pet.signature = typeof v === "string" ? v : null;
        continue;
      }

      if (isFileLike(v)) {
        const list = asList(v);
        if (/pet\s*profile|pet.*photo|profile\s*pic/.test(t)) {
          pet.photo = list[0];
          continue;
        }
        // Vaccination images/certificates → match by vaccine name
        if (/vaccin/.test(t)) {
          const vaccineName =
            String(q.question || "")
              .replace(/vaccin\w*/gi, "")
              .replace(
                /\b(certificate|record|proof|image|document|upload|photo|pic|picture|copy|file)\b/gi,
                "",
              )
              .replace(/\s+/g, " ")
              .trim() || "Vaccination";

          let rec = pet.vaccination_records.find(
            (r) => norm(r.vaccine) === norm(vaccineName),
          );
          if (!rec) {
            rec = {
              vaccine: vaccineName,
              date_given: "",
              next_due: null,
              notes: "",
              documents: [],
            };
            pet.vaccination_records.push(rec);
          }
          rec.documents = [...(rec.documents || []), ...list];
          continue;
        }
        const match = PET_FILE_KEYS.find(([rx]) => rx.test(t));
        const bkey = match ? match[1] : "documents";
        pet[bkey] = [...asList(pet[bkey]), ...list];
        continue;
      }

      if (type === "yes_no") {
        pet.custom[q._id] = { label: q.question, value: v };
        continue;
      }
      if (
        /vaccin/.test(t) &&
        /\b(date|given)\b/.test(t) &&
        !/status|next|due|record|image|certificate|proof|notes/.test(t)
      ) {
        const vaccineName =
          String(q.question || "")
            .replace(/vaccin\w*/gi, "")
            .replace(/\b(date|given|on|of)\b/gi, "")
            .replace(/\s+/g, " ")
            .trim() || "Vaccination";

        pet.vaccination_records = [
          ...(pet.vaccination_records || []),
          {
            vaccine: vaccineName,
            date_given: v,
            next_due: null,
            notes: "",
            documents: [],
          },
        ];
        continue;
      }
      // gate questions are not data
      if (
        applyPetMapsTo(pet, q, v, {
          isFileLike,
          toDocs,
          asList,
          labelFor,
          idx: i,
        })
      ) {
        continue;
      }
      if (/pet\s*name/.test(t)) pet.name = v;
      else if (/pet\s*type/.test(t)) pet.pet_type = labelFor("pet_type", v, i);
      else if (/\bbreed\b/.test(t)) pet.breed = labelFor("breed", v, i);
      else if (/\bsize\b/.test(t)) pet.size = labelFor("size", v, i);
      else if (/\bgender\b|\bsex\b/.test(t)) pet.gender = v;
      else if (/\bbirthday\b|\bdob\b|date\s*of\s*birth/.test(t))
        pet.birthday = v;
      else if (/\bage\b/.test(t)) pet.age = String(v);
      else if (/\bweight\b/.test(t) && !/unit/.test(t)) {
        const n = Number(v);
        pet.weight_kg = Number.isNaN(n) ? v : n;
      } else if (/\bcolou?r\b/.test(t)) pet.color = Array.isArray(v) ? v : [v];
      else if (/hair\s*type/.test(t)) pet.hair_type = asStr(v);
      else if (/hair\s*length/.test(t)) pet.hair_length = asStr(v);
      else if (/neutered|spayed/.test(t)) pet.neutered_status = v;
      else if (
        /vaccin/.test(t) &&
        /\b(date|given)\b/.test(t) &&
        !/status|next|due|record|image|certificate|proof|notes/.test(t)
      ) {
        const vaccineName =
          String(q.question || "")
            .replace(/vaccin\w*/gi, "")
            .replace(/\b(date|given|on|of)\b/gi, "")
            .replace(/\s+/g, " ")
            .trim() || "Vaccination";
        pet.vaccination_records = [
          ...(pet.vaccination_records || []),
          { vaccine: vaccineName, date_given: v, next_due: null, notes: "" },
        ];
      } else if (/vaccination\s*status/.test(t)) pet.vaccination_status = v;
      else if (/\bvaccine\b/.test(t) && !/status|record|notes/i.test(t))
        pet.vaccinations = Array.isArray(v) ? v : [v];
      else if (/custom\s*medical/.test(t)) pet.custom_medical = v;
      else if (/medical\s*condition/.test(t))
        pet.medical_conditions = Array.isArray(v) ? v : [v];
      else if (/\ballerg/.test(t) && !/details|severity|notes/i.test(t))
        pet.allergies = Array.isArray(v) ? v : [v];
      else if (/diet\s*type|meal\s*type/.test(t))
        pet.meal_type = Array.isArray(v) ? v : v ? [v] : [];
      else if (/meals?\s*per\s*day/.test(t)) pet.meals_per_day = String(v);
      else if (/walks?\s*per\s*day/.test(t)) pet.walks_per_day = String(v);
      else if (/behaviou?r/.test(t)) pet.behavior_notes = asStr(v);
      else if (/special\s*care/.test(t)) pet.special_care_instructions = v;
      else if (/vet.*name|veterinarian.*name/i.test(t))
        pet.veterinarian.name = v;
      else if (/clinic/.test(t)) pet.veterinarian.clinic = v;
      else if (/vet.*phone|veterinarian.*phone/i.test(t))
        pet.veterinarian.phone = v;
      else if (/pet\s*profile|pet.*photo/.test(t)) pet.image = v;
      else {
        pet.custom[q._id || q.question] = { label: q.question, value: v };
      }
    }
  }

  /* Meals from DietBlock items */
  for (let i = 0; i < petCount; i++) {
    const dietItems = answers[`diet__${i}__items`];
    if (Array.isArray(dietItems) && dietItems.length) {
      const cleaned = dietItems
        .filter(
          (m) => m && (m.what || m.when?.length || m.how || m.instructions),
        )
        .map((m) => ({
          name: m.what || "",
          when: Array.isArray(m.when) ? m.when : m.when ? [m.when] : [],
          quantity: m.how || "",
          notes: m.instructions || "",
        }));
      if (cleaned.length) pets[i].meals = cleaned;
    }

    const medItems = answers[`meds__${i}__items`];
    if (Array.isArray(medItems) && medItems.length) {
      const cleaned = medItems
        .filter((m) => m && (m.name || m.dosage || m.when?.length))
        .map((m) => ({
          name: m.name || "",
          dosage: m.dosage || "",
          time: Array.isArray(m.when) ? m.when.join(", ") : m.when || "",
          instructions: m.instructions || "",
          photos: Array.isArray(m.photos)
            ? m.photos
            : m.photos
              ? [m.photos]
              : [],
        }));
      if (cleaned.length) pets[i].ongoing_medications = cleaned;
    }

    /* Vaccination: saved (locked) + newly added */
    {
      const saved = Array.isArray(answers[`vacc__${i}__saved`])
        ? answers[`vacc__${i}__saved`]
        : [];
      const vaccItems = Array.isArray(answers[`vacc__${i}__items`])
        ? answers[`vacc__${i}__items`]
        : [];

      const isFileT = (q) =>
        ["file_upload", "multi_file_upload", "image_upload"].includes(
          (q.answerType || "").toLowerCase(),
        );
      const isDateQ = (q) =>
        /vaccin/.test(qText(q)) &&
        /\b(date|given)\b/.test(qText(q)) &&
        !/status|next|due|record|image|certificate|proof|notes/.test(qText(q));
      const vaccNameOf = (q) =>
        String(q.question || "")
          .replace(/vaccin\w*/gi, "")
          .replace(
            /\b(certificate|record|proof|image|document|upload|photo|pic|picture|copy|file|date|given|on|of)\b/gi,
            "",
          )
          .replace(/\s+/g, " ")
          .trim();

      const dateQs = petQuestions.filter(isDateQ);
      const fileQs = petQuestions.filter(
        (q) => isFileT(q) && /vaccin/.test(qText(q)),
      );

      const added = [];
      for (const row of vaccItems) {
        if (!row) continue;
        const byVaccine = new Map();
        const get = (name) => {
          const k = norm(name) || "vaccination";
          if (!byVaccine.has(k))
            byVaccine.set(k, {
              vaccine: name || "Vaccination",
              date_given: "",
              next_due: null,
              notes: "",
              documents: [],
            });
          return byVaccine.get(k);
        };
        for (const dq of dateQs)
          if (row[dq._id]) get(vaccNameOf(dq)).date_given = row[dq._id];
        for (const fq of fileQs)
          if (isFileLike(row[fq._id]))
            get(vaccNameOf(fq)).documents.push(...asList(row[fq._id]));
        added.push(...byVaccine.values());
      }

      if (saved.length || added.length)
        pets[i].vaccination_records = [...saved, ...added];
    }
  }

  /* one record per vaccine: date + certificate together (first-time submit only) */
  pets.forEach((p, i) => {
    const hasSaved = (answers[`vacc__${i}__saved`] || []).length > 0;
    if (!hasSaved)
      p.vaccination_records = mergeVaccRecords(p.vaccination_records);
  });

  /* tidy medical / allergy lists and merge the custom condition */
  const NOT_A_VALUE = /^(yes|no|none|nil|n\/a|na)$/i;
  for (const p of pets) {
    let conds = (p.medical_conditions || []).filter(
      (c) => !NOT_A_VALUE.test(String(c).trim()),
    );
    if (p.custom_medical) {
      conds = [
        ...conds.filter((c) => !/^other$/i.test(String(c).trim())),
        String(p.custom_medical).trim(),
      ];
    }
    p.medical_conditions = conds;
    p.allergies = (p.allergies || []).filter(
      (a) => !NOT_A_VALUE.test(String(a).trim()),
    );
    delete p.custom_medical;
    finalizeMappedPet(p);
  }

  /* consent section: declaration, signature, other answers */
  const consentAnswers = [];
  let consentAccepted = !!consent?.agreed;
  let consentSignature = consent?.signature || "";
  for (const q of consentQ || []) {
    if (
      q.answerType === "section_heading" ||
      q.answerType === "description_text"
    )
      continue;
    const v = answers[q._id];
    if (isEmpty(v)) continue;
    const t = qText(q);

    if ((q.answerType || "").toLowerCase() === "signature") {
      if (typeof v === "string") consentSignature = v;
      continue;
    }
    if (isFileLike(v)) {
      customer.documents.push(...toDocs(v, q, "document"));
      continue;
    }
    if (/owner\s*declaration/.test(t)) consentAccepted = true;

    consentAnswers.push({
      question: q.question,
      answer: Array.isArray(v) ? v.join(", ") : String(v),
    });
  }
  if (consentSignature) customer.signature = consentSignature;

  const filledPets = pets.filter(
    (p) => p.name || p.pet_type || p.breed || p.documents?.length,
  );

  // ── NEW: build the snapshot payload the backend needs ──
  const slimQ = (q) => ({
    _id: q._id,
    question: q.question,
    answerType: q.answerType,
    maps_to: q.maps_to || q.mapsTo || "custom",
    field_uid: q.field_uid || q.fieldUid || null,
    order: q.order ?? 0,
    sectionName: q.sectionName || null,
    options: q.options || [],
  });

  const schemaOut = {
    customerQuestions: (schema?.customerQuestions || customerQuestions).map(
      slimQ,
    ),
    petQuestions: (schema?.petQuestions || petQuestions).map(slimQ),
    consentQuestions: (schema?.consentQuestions || consentQ || []).map(slimQ),
  };

  const rawAnswersOut = {};
  Object.keys(answers || {}).forEach((k) => {
    const v = answers[k];
    if (v === undefined || v === null) return;
    rawAnswersOut[k] = v;
  });

  return {
    customer,
    pets: filledPets.length ? filledPets : pets,
    consent: {
      accepted: consentAccepted,
      signature: consentSignature,
      accepted_at: new Date().toISOString(),
      answers: consentAnswers,
    },
    // ── NEW fields forwarded to backend ──
    schema: schemaOut,
    rawAnswers: rawAnswersOut,
    wants_account: !!createAccount,
  };
};

/* ---------- HYDRATE (saved submission → answers) ---------- */
const hydrateAnswersFromSubmission = ({
  submission,
  customerQuestions,
  petQuestions,
  consentQ,
}) => {
  const answers = {};
  const cust = submission.customer || {};
  const put = (key, val) => {
    if (val === undefined || val === null || val === "") return;
    answers[key] = val;
  };

  /* Convert a stored label ("Male", "Non-Vegetarian") back to the
     option's value ("60f…") so radio/checkbox state lights up. */
  const toOpt = (q, val) => {
    if (val == null) return val;
    const opts = normalizeOptions(q);
    if (!opts.length) return val;
    if (Array.isArray(val)) return val.map((v) => toOpt(q, v));
    if (opts.some((o) => String(o.value) === String(val))) return val;
    const m = opts.find((o) => norm(o.label) === norm(val));
    return m ? m.value : val;
  };

  /* Returns an array of option-values for multi-select questions,
     tolerating the joined-string form saved by buildSubmitPayload. */
  const multiVal = (q, raw) => {
    const multi = ["multiple_choice", "multiselect", "checkbox"].includes(
      (q.answerType || "").toLowerCase(),
    );
    if (multi) {
      if (Array.isArray(raw)) return toOpt(q, raw);
      if (typeof raw === "string" && raw.trim()) {
        const parts = raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        return toOpt(q, parts);
      }
      return raw;
    }
    return toOpt(q, raw);
  };

  
  /* ── Customer ── */
  for (const q of customerQuestions) {
    if (q.answerType === "section_heading") continue;
    const t = qText(q);
    const type = (q.answerType || "").toLowerCase();

    if (type === "signature") {
      if (cust.signature) answers[q._id] = cust.signature;
      continue;
    }
    if (
      ["file_upload", "multi_file_upload", "image_upload"].includes(type) &&
      isFileLike(cust.documents)
    ) {
      const pref = `${q.question}:`;
      const cat = docTypeFor(q.question);
      const pool = cust.documents || [];

      let matched = pool.filter((d) => String(d.name || "").startsWith(pref));
      if (!matched.length) {
        matched = pool.filter((d) => (d.category || "") === cat);
      }

      if (matched.length) {
        const stripped = matched.map((d) => ({
          ...d,
          _saved: true,
          name: String(d.name || "").startsWith(pref)
            ? String(d.name).slice(pref.length).trim()
            : d.name,
        }));
        answers[q._id] = /multi|file_upload/i.test(type)
          ? stripped
          : stripped[0];
      }
      continue;
    }

    if (/whats\s*app/.test(t)) put(q._id, cust.whatsapp_number);
    else if (/\bphone\b|\bmobile\b/.test(t)) put(q._id, cust.phone);
    else if (/\bfirst\s*name\b/.test(t)) put(q._id, cust.firstname);
    else if (/\blast\s*name\b/.test(t)) put(q._id, cust.lastname);
    else if (/\bemail\b/.test(t)) put(q._id, cust.email);
    else if (/address\s*(line\s*)?1|home\s*address/.test(t))
      put(q._id, cust.address?.address1);
    else if (/address\s*(line\s*)?2/.test(t))
      put(q._id, cust.address?.address2);
    else if (/landmark/.test(t)) put(q._id, cust.address?.landmark);
    else if (/\bcity\b/.test(t)) put(q._id, cust.address?.city);
    else if (/\bstate\b/.test(t)) put(q._id, cust.address?.state);
    else if (/pin\s*code|pincode|\bzip\b/.test(t))
      put(q._id, cust.address?.zip);
    else if (/customer\s*ref|referred\s*by|reference/.test(t))
      put(q._id, cust.reference_source);
    else if (/services?\s*interest/.test(t)) {
      const opts = normalizeOptions(q);
      const toVal = (v) => {
        const m = opts.find((o) => norm(o.label) === norm(v));
        return m ? m.value : v;
      };
      const raw = cust.services_interested_in;
      put(q._id, Array.isArray(raw) ? raw.map(toVal) : raw);
    } else if (/\bnotes?\b|comments?/.test(t)) put(q._id, cust.notes);
  }

  if (cust.custom && typeof cust.custom === "object") {
    for (const entry of Object.values(cust.custom)) {
      if (!entry || !entry.label) continue;
      const q = customerQuestions.find(
        (qq) => norm(qq.question) === norm(entry.label),
      );
      if (!q) continue;
      let v = entry.value;
      if (Array.isArray(v)) v = v.join(", ");
      else if (v && typeof v === "object") v = String(v.value ?? v.label ?? "");
      put(q._id, v);
    }
  }

  /* ── Pets ── */
  const pets = Array.isArray(submission.pets) ? submission.pets : [];
  pets.forEach((pet, i) => {
    for (const q of petQuestions) {
      if (q.answerType === "section_heading") continue;
      const t = qText(q);
      const type = (q.answerType || "").toLowerCase();
      const key = petKey(q._id, i);

      if (type === "signature") {
        if (pet.signature) answers[key] = pet.signature;
        continue;
      }
      if (type === "yes_no") {
        const saved = pet.custom && pet.custom[q._id];
        const savedVal = saved && saved.value;
        answers[key] = savedVal || "No";
        continue;
      }

      if (/pet\s*profile|pet.*photo|profile\s*pic/.test(t)) {
  if (pet.photo || pet.image) {
    const url = pet.photo || pet.image;
    answers[key] = {
      url,
      name: 'Pet Photo',         
      _saved: true,               
      category: 'pet_photo',
      type: 'image',
      key: pet.image_key || null,
    };
  }
  continue;
}
      if (
        ["file_upload", "multi_file_upload", "image_upload"].includes(type) &&
        isFileLike(pet.documents)
      ) {
        const pref = `${q.question}:`;
        const cat = docTypeFor(q.question);
        const pool = pet.documents || [];

        let matched = pool.filter((d) => String(d.name || "").startsWith(pref));
        if (!matched.length) {
          matched = pool.filter((d) => (d.category || "") === cat);
        }

        if (matched.length) {
          const stripped = matched.map((d) => ({
            ...d,
            _saved: true,
            name: String(d.name || "").startsWith(pref)
              ? String(d.name).slice(pref.length).trim()
              : d.name,
          }));
          answers[key] = /multi|file_upload/i.test(type)
            ? stripped
            : stripped[0];
        }
        continue;
      }

      if (/pet\s*name/.test(t)) put(key, pet.name);
      else if (/pet\s*type/.test(t)) put(key, pet.pet_type);
      else if (/\bbreed\b/.test(t)) put(key, pet.breed);
      else if (/\bsize\b/.test(t)) put(key, pet.size);
      else if (/\bgender\b|\bsex\b/.test(t)) put(key, toOpt(q, pet.gender));
      else if (/\bbirthday\b|\bdob\b|date\s*of\s*birth/.test(t))
        put(
          key,
          typeof pet.birthday === "string"
            ? pet.birthday.slice(0, 10)
            : pet.birthday,
        );
      else if (/\bage\b/.test(t)) put(key, pet.age);
      else if (/\bweight\b/.test(t) && !/unit/.test(t))
        put(key, pet.weight_kg != null ? String(pet.weight_kg) : "");
      else if (/\bcolou?r\b/.test(t)) put(key, multiVal(q, pet.color));
      else if (/hair\s*type/.test(t)) put(key, multiVal(q, pet.hair_type));
      else if (/hair\s*length/.test(t)) put(key, multiVal(q, pet.hair_length));
      else if (/neutered|spayed/.test(t))
        put(key, multiVal(q, pet.neutered_status));
      else if (/vaccination\s*status/.test(t)) put(key, pet.vaccination_status);
      else if (
        /vaccin/.test(t) &&
        /\b(date|given)\b/.test(t) &&
        !/status|next|due|record|image|certificate|proof|notes/.test(t)
      ) {
        const vaccineName =
          String(q.question || "")
            .replace(/vaccin\w*/gi, "")
            .replace(/\b(date|given|on|of)\b/gi, "")
            .replace(/\s+/g, " ")
            .trim() || "Vaccination";
        const rec = (pet.vaccination_records || []).find(
          (r) => norm(r.vaccine) === norm(vaccineName),
        );
        if (rec?.date_given) answers[key] = rec.date_given;
      } else if (/\bvaccine\b/.test(t) && !/status|record|notes/i.test(t))
        put(key, toOpt(q, pet.vaccinations));
      else if (isMedicalConditionsQ(q)) {
        const arr = Array.isArray(pet.medical_conditions)
          ? pet.medical_conditions
          : [];
        const opts = normalizeOptions(q);
        const mapped = [];
        let customText = "";
        for (const c of arr) {
          const m = opts.find(
            (o) => String(o.value) === String(c) || norm(o.label) === norm(c),
          );
          if (m) mapped.push(m.value);
          else customText = String(c); // anything that isn't an option = the custom text
        }
        if (customText) {
          const otherOpt = opts.find((o) =>
            /^other$/i.test(String(o.label).trim()),
          );
          if (otherOpt) mapped.push(otherOpt.value); // re-check "Other"
          const customQ = petQuestions.find(isCustomMedCondQ);
          if (customQ) answers[petKey(customQ._id, i)] = customText; // refill the text box
        }
        put(key, mapped);
      } else if (/\ballerg/.test(t) && !/details|severity|notes/i.test(t))
        put(key, toOpt(q, pet.allergies));
      else if (/diet\s*type|meal\s*type/.test(t))
        put(key, toOpt(q, pet.meal_type));
      else if (/meals?\s*per\s*day/.test(t)) put(key, pet.meals_per_day);
      else if (/walks?\s*per\s*day/.test(t)) put(key, pet.walks_per_day);
      else if (/behaviou?r/.test(t)) {
        // Behaviour may live in pet.behavior_notes OR in pet.custom["pet.behaviour"]
        let raw = pet.behavior_notes;
        if (isEmpty(raw)) {
          const c = pet.custom?.["pet.behaviour"] || pet.custom?.[q._id];
          raw = c?.value;
        }
        put(key, multiVal(q, raw));
      } else if (/special\s*care/.test(t))
        put(key, pet.special_care_instructions);
      else if (/vet.*name|veterinarian.*name/i.test(t))
        put(key, pet.veterinarian?.name);
      else if (/clinic/.test(t)) put(key, pet.veterinarian?.clinic);
      else if (/vet.*phone|veterinarian.*phone/i.test(t))
        put(key, pet.veterinarian?.phone);
    }

    if (pet.custom && typeof pet.custom === "object") {
      const savedVacc = Array.isArray(pet.vaccination_records)
        ? pet.vaccination_records
        : [];
      if (savedVacc.length) {
        for (const q of petQuestions) {
          const t = qText(q);
          if (!/vaccin/.test(t)) continue;
          if (!/\b(date|given)\b/.test(t)) continue;
          if (/status|next|due|record|image|certificate|proof|notes/.test(t))
            continue;

          const vaccineName = String(q.question || "")
            .replace(/vaccin\w*/gi, "")
            .replace(/\b(date|given|on|of)\b/gi, "")
            .replace(/\s+/g, " ")
            .trim();

          const rec = savedVacc.find(
            (r) =>
              String(r.vaccine || "")
                .toLowerCase()
                .trim() === vaccineName.toLowerCase().trim(),
          );
          if (rec?.date_given) {
            const d =
              typeof rec.date_given === "string"
                ? rec.date_given.slice(0, 10)
                : new Date(rec.date_given).toISOString().slice(0, 10);
            put(petKey(q._id, i), d);
          }
        }
      }
      for (const entry of Object.values(pet.custom)) {
        if (!entry || !entry.label) continue;
        const L = norm(entry.label);

        // Rabies / DHPPi / any "<Vaccine> Vaccination Date"
        if (
          /vaccin/.test(L) &&
          /\b(date|given)\b/.test(L) &&
          !/status|next|due|record|notes/.test(L)
        ) {
          const vName = L.replace(/vaccin\w*/g, "")
            .replace(/\b(date|given|on|of)\b/g, "")
            .replace(/\s+/g, " ")
            .trim();

          const dateQ = petQuestions.find((qq) => {
            const qt = qText(qq);
            if (!/vaccin/.test(qt)) return false;
            if (!/\b(date|given)\b/.test(qt)) return false;
            if (/status|next|due|record|image|certificate|proof|notes/.test(qt))
              return false;
            const qName = qt
              .replace(/vaccin\w*/g, "")
              .replace(/\b(date|given|on|of)\b/g, "")
              .replace(/\s+/g, " ")
              .trim();
            return (
              qName === vName || qName.includes(vName) || vName.includes(qName)
            );
          });

          if (dateQ) {
            let val = entry.value;
            if (val && typeof val === "object") val = String(val.value ?? "");
            if (val) {
              // trim "2026-10-06T00:00:00.000Z" → "2026-10-06"
              const d =
                typeof val === "string" && val.length >= 10
                  ? val.slice(0, 10)
                  : val;
              put(petKey(dateQ._id, i), d);
            }
            continue;
          }
        }

        // generic label match
        // generic label match
        const q =
          petQuestions.find((qq) => norm(qq.question) === L) ||
          petQuestions.find((qq) => norm(qq.question).includes(L)) ||
          petQuestions.find((qq) => L.includes(norm(qq.question)));
        if (!q) continue;

        const ansType = (q.answerType || "").toLowerCase();
        const isMulti = ["multiple_choice", "multiselect", "checkbox"].includes(
          ansType,
        );

        let v = entry.value;
        if (isMulti && Array.isArray(v)) {
          /* map stored labels → option values, keep as array */
          put(petKey(q._id, i), toOpt(q, v));
          continue;
        }
        if (Array.isArray(v)) v = v.join(", ");
        else if (v && typeof v === "object")
          v = String(v.value ?? v.label ?? "");
        put(petKey(q._id, i), v);
      }
    }

    /* Seed the vaccination repeater from the saved submission — independent
       of meals. Any record that already exists becomes an `_existing` row,
       so the UI shows it locked and only allows adding more. */
    // const savedVacc = Array.isArray(pet.vaccination_records)
    //   ? pet.vaccination_records
    //   : [];
    // if (savedVacc.length) {
    //   const dateQ = petQuestions.find(
    //     (q) =>
    //       /vaccin/.test(qText(q)) &&
    //       /\b(date|given)\b/.test(qText(q)) &&
    //       !/status|next|due|record|image|certificate|proof|notes/.test(
    //         qText(q),
    //       ),
    //   );
    //   const nameQ = petQuestions.find(
    //     (q) =>
    //       /\bvaccine\b/.test(qText(q)) &&
    //       !/status|record|notes/i.test(qText(q)),
    //   );
    //   answers[`vacc__${i}__items`] = savedVacc.map((r) => {
    //     const row = { _existing: true };
    //     if (dateQ && r.date_given) row[dateQ._id] = r.date_given;
    //     if (nameQ && r.vaccine) row[nameQ._id] = r.vaccine;
    //     return row;
    //   });
    // }
    const dateQs = petQuestions.filter((q) => {
      const t = qText(q);
      return (
        /vaccin/.test(t) &&
        /\b(date|given)\b/.test(t) &&
        !/status|next|due|record|image|certificate|proof|notes/.test(t)
      );
    });

    const nameOfDateQ = (q) =>
      qText(q)
        .replace(/vaccin\w*/g, "")
        .replace(/\b(date|given|on|of)\b/g, "")
        .replace(/\s+/g, " ")
        .trim();

    const pickDateQ = (vaccineName) => {
      const v = String(vaccineName || "")
        .toLowerCase()
        .trim();
      if (!v) return dateQs[0] || null;
      return (
        dateQs.find((q) => nameOfDateQ(q) === v) ||
        dateQs.find(
          (q) => nameOfDateQ(q).includes(v) || v.includes(nameOfDateQ(q)),
        ) ||
        null
      );
    };

    const trimDate = (v) => {
      if (v == null) return "";
      if (typeof v === "string" && v.length >= 10) return v.slice(0, 10);
      if (v instanceof Date) return v.toISOString().slice(0, 10);
      return String(v);
    };

    /* Saved vaccination records → read-only list */
    const savedRecs = Array.isArray(pet.vaccination_records)
      ? [...pet.vaccination_records]
      : [];

    for (const entry of Object.values(pet.custom || {})) {
      if (!entry?.label) continue;
      const L = norm(entry.label);
      if (
        !/vaccin/.test(L) ||
        !/\b(date|given)\b/.test(L) ||
        /status|next|due|record|notes/.test(L)
      )
        continue;
      let val = entry.value;
      if (val && typeof val === "object") val = String(val.value ?? "");
      if (!val) continue;
      const vName =
        L.replace(/vaccin\w*/g, "")
          .replace(/\b(date|given|on|of)\b/g, "")
          .replace(/\s+/g, " ")
          .trim() || "Vaccination";
      const d = trimDate(val);
      if (
        savedRecs.some(
          (r) =>
            norm(r.vaccine) === norm(vName) && trimDate(r.date_given) === d,
        )
      )
        continue;
      savedRecs.push({
        vaccine: vName,
        date_given: d,
        next_due: null,
        notes: "",
        documents: [],
      });
    }

    /* vaccination files saved in pet.documents (older submissions) */
const vaccNameFrom = (s) =>
  String(s || "")
    .replace(/vaccin\w*/gi, "")
    .replace(
      /\b(certificate|record|proof|image|document|upload|photo|pic|picture|copy|file|date|given|on|of)\b/gi,
      "",
    )
    .replace(/\s+/g, " ")
    .trim();

for (const d of Array.isArray(pet.documents) ? pet.documents : []) {
  const nm = String(d?.name || "");
  if (!/vaccin/i.test(nm) && d?.category !== "vaccination") continue;
  const hasPrefix = nm.includes(":");
  savedRecs.push({
    vaccine: vaccNameFrom(hasPrefix ? nm.split(":")[0] : "") || "Vaccination",
    date_given: "",
    next_due: null,
    notes: "",
    documents: [
      { ...d, name: hasPrefix ? nm.slice(nm.indexOf(":") + 1).trim() : nm },
    ],
  });
}

const dbRecs = Array.isArray(pet.vaccination_records)
  ? pet.vaccination_records
  : [];
const extras = savedRecs.slice(dbRecs.length); // everything pushed after the copy

/* skip legacy extras whose file is already inside a stored record */
const knownKeys = new Set(
  dbRecs.flatMap((r) => (r.documents || []).map((d) => d.key || d.url)),
);
const freshExtras = extras
  .map((r) => ({
    ...r,
    documents: (r.documents || []).filter(
      (d) => !knownKeys.has(d.key || d.url),
    ),
  }))
  .filter((r) => r.documents.length > 0 || r.date_given);

answers[`vacc__${i}__saved`] = [...dbRecs, ...mergeVaccRecords(freshExtras)];

    if (Array.isArray(pet.meals) && pet.meals.length) {
      answers[`diet__${i}__items`] = pet.meals.map((m) => ({
        what: m.name || "",
        when:
          m.time || (Array.isArray(m.when) ? m.when.join(", ") : m.when) || "",
        how: m.quantity || "",
        instructions: m.notes || "",
      }));
    }
    /* re-arm the yes/no gates so the detail blocks reopen */
    const yesValueFor = (q) => {
      const m = normalizeOptions(q).find((o) =>
        /^yes$/i.test(String(o.label).trim()),
      );
      return m ? m.value : "Yes";
    };

    if (
      Array.isArray(pet.medical_conditions) &&
      pet.medical_conditions.length
    ) {
      const ynQ = petQuestions.find(isMedicalYNQ);
      if (ynQ) answers[petKey(ynQ._id, i)] = yesValueFor(ynQ);
    }
    if (
      Array.isArray(pet.ongoing_medications) &&
      pet.ongoing_medications.length
    ) {
      const ynQ = petQuestions.find(isOnMedicationQ);
      if (ynQ) answers[petKey(ynQ._id, i)] = yesValueFor(ynQ);
    }
    if (
      Array.isArray(pet.ongoing_medications) &&
      pet.ongoing_medications.length
    ) {
      answers[`meds__${i}__items`] = pet.ongoing_medications.map((m) => {
       
        const rawWhen = m.time ?? m.when ?? "";
        const when = Array.isArray(rawWhen)
          ? rawWhen
          : typeof rawWhen === "string" && rawWhen.trim()
            ? rawWhen
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];

        return {
          name: m.name || "",
          dosage: m.dosage || "",
          when, 
          instructions: m.instructions || m.notes || "", 
          photos: Array.isArray(m.documents)
            ? m.documents
            : Array.isArray(m.photos)
              ? m.photos
              : [],
        };
      });
    }
  });

  /* ── Consent ── */
  const consent = {
    agreed: !!submission.consent?.accepted,
    signature: submission.consent?.signature || "",
  };
  for (const q of consentQ || []) {
    if (
      q.answerType === "section_heading" ||
      q.answerType === "description_text"
    )
      continue;
    const ans = (submission.consent?.answers || []).find(
      (a) => norm(a.question) === norm(q.question),
    );
    if (ans) answers[q._id] = ans.answer;
  }

  return { answers, consent };
};;

/* ---------- CONTEXT ---------- */
const FormCtx = createContext(null);
export const useForm = () => {
  const ctx = useContext(FormCtx);
  if (!ctx) throw new Error("useForm must be used inside <Form>");
  return ctx;
};

function FormProvider({ vendorId, formId, children }) {
  const [phase, setPhase] = useState("auth");
  const [step, setStep] = useState(0);
  const [phone, setPhone] = useState("");
  const [vendor, setVendor] = useState(null);
  const [schema, setSchema] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [petTypes, setPetTypes] = useState([]);
  const [petCount, setPetCount] = useState(1);
  const [petRef, setPetRef] = useState({});
  const [activePet, setActivePet] = useState(0);
  const [consent, setConsent] = useState({ agreed: false, signature: "" });
  const [consentErr, setConsentErr] = useState("");
  const [submitErr, setSubmitErr] = useState(null);
  const [existingSubmissionId, setExistingSubmissionId] = useState(null);
  const [createAccount, setCreateAccount] = useState(false);
  const [accountAlreadyCreated, setAccountAlreadyCreated] = useState(false);

  // const [pendingPrefill, setPendingPrefill] = useState(null);

  /* in-flight upload counter — blocks Continue / Submit */
  const [busyCount, setBusyCount] = useState(0);
  const onBusy = useCallback(
    (delta) => setBusyCount((c) => Math.max(0, c + delta)),
    [],
  );

  useEffect(() => {
    if (!vendorId) return;
    let c = false;
    getVendorProfile(vendorId)
      .then(
        (d) =>
          !c &&
          setVendor({
            name: d?.brandName || d?.companyName || d?.name || "",
            logo: d?.logo || d?.logoUrl || d?.brandLogo || "",
          }),
      )
      .catch(() => !c && setVendor(null));
    return () => {
      c = true;
    };
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    let c = false;
    getPetTypes(vendorId).then((l) => !c && setPetTypes(l));
    return () => {
      c = true;
    };
  }, [vendorId]);

  const questions = useMemo(() => {
    const sorted = ((schema && schema.questions) || [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const out = [];
    let hiding = false;
    sorted.forEach((q) => {
      const m = HIDDEN_PATTERNS.some((r) => r.test(q.question || ""));

      if (q.answerType === "section_heading") {
        hiding = m;
        if (m) return;
        // Keep the heading for section grouping even when disabled, but
        // strip its associated_services so it no longer hides its
        // still-enabled children (e.g. "Medical Conditions" under a
        // disabled "Health and Medical History" heading).
        out.push(q.enabled === false ? { ...q, associated_services: [] } : q);
        return;
      }

      // Skip any non-heading question the form builder has disabled.
      if (q.enabled === false) return;

      if (hiding || m) return;
      out.push(q);
    });
    return out;
  }, [schema]);

  const sections = useMemo(() => {
    const out = [];
    let cur = [];
    questions.forEach((q) => {
      if (q.answerType === "section_heading") {
        if (cur.length) out.push(cur);
        cur = [q];
      } else cur.push(q);
    });
    if (cur.length) out.push(cur);
    return out;
  }, [questions]);

  const consentSectionIdxs = useMemo(() => {
    const idxs = [];
    sections.forEach((s, i) => {
      if (s[0] && isConsentSection(s[0])) idxs.push(i);
    });
    return idxs;
  }, [sections]);
  const consentSectionSet = useMemo(
    () => new Set(consentSectionIdxs),
    [consentSectionIdxs],
  );

  const petSectionIdxs = useMemo(() => {
    const idxs = [];
    let inPetCtx = false;
    sections.forEach((s, i) => {
      if (consentSectionSet.has(i)) return;
      const heading =
        s[0]?.answerType === "section_heading"
          ? String(s[0].question || "").toLowerCase()
          : "";
      const explicitlyPet = /pet/.test(heading) && !/parent/.test(heading);
      const explicitlyCustomer = /customer|parent|address|contact|billing/.test(
        heading,
      );
      const hasPetQs = s.some(
        (q) => isPetTypeQ(q) || isBreedQ(q) || isPetNameQ(q),
      );
      if (explicitlyPet || hasPetQs) {
        inPetCtx = true;
        idxs.push(i);
        return;
      }
      if (explicitlyCustomer) {
        inPetCtx = false;
        return;
      }
      if (inPetCtx) idxs.push(i);
    });
    if (idxs.length === 0) {
      const f = sections.findIndex((s) => s.some((q) => isPetTypeQ(q)));
      if (f !== -1) idxs.push(f);
    }
    return idxs;
  }, [sections, consentSectionSet]);

  const petSectionSet = useMemo(
    () => new Set(petSectionIdxs),
    [petSectionIdxs],
  );
  const consentQ = useMemo(
    () => sections.filter((_, i) => consentSectionSet.has(i)).flat(),
    [sections, consentSectionSet],
  );

  const customerQuestions = useMemo(() => {
    const out = [];
    sections.forEach((s, i) => {
      if (!petSectionSet.has(i) && !consentSectionSet.has(i))
        s.forEach((q) => out.push(q));
    });
    return arrangeDobAge(out);
  }, [sections, petSectionSet, consentSectionSet]);

  const petQuestions = useMemo(() => {
    const out = [];
    petSectionIdxs.forEach((i) => sections[i].forEach((q) => out.push(q)));
    return arrangeDobAge(out);
  }, [sections, petSectionIdxs]);

  /* service categories first (needed by visiblePetQuestions) */
    const serviceCategories = useMemo(() => {
      const q = customerQuestions.find(isServiceInterestQ);
      const base = {
        grooming: false,
        boarding: false,
        vet: false,
        other: false,
      };
      if (!q) return { grooming: true, boarding: true, vet: true, other: true };
      const v = answers[q._id];
      const arr = Array.isArray(v) ? v : v ? [v] : [];
      if (arr.length === 0) return base;

      /* Match against BOTH the option value and the option label so it
       works whether `answers` stores IDs (post-hydrate) or labels. */
      const opts = normalizeOptions(q);
      const text = arr
        .map((x) => {
          const m = opts.find((o) => String(o.value) === String(x));
          const label = m ? m.label : String(x);
          return `${label} ${x}`.toLowerCase();
        })
        .join(" | ");

            const grooming = /groom/.test(text);
            const boarding = /board|day\s*care|daycare/.test(text);
            const vet = /veterin/.test(text);
            const other = /other/.test(text);

            return { grooming, boarding, vet, other };
    }, [customerQuestions, answers]);

 

      const visiblePetQuestions = useMemo(() => {
    const cats = serviceCategories;

    /* Map an associated_services label ("Boarding", "Grooming",
       "Veterinary", "Other") → which cats flag to check. */
    const catFor = (svc) => {
      const s = String(svc || "").toLowerCase();
      if (/groom/.test(s)) return "grooming";
      if (/board|day\s*care|daycare/.test(s)) return "boarding";
      if (/veterin/.test(s)) return "vet";
      if (/other/.test(s)) return "other";
      return null;
    };

    const out = [];
    let show = true;
    let inMedBlock = false;
    let inDietBlock = false;

    petQuestions.forEach((q) => {
      if (q.answerType === "section_heading") {
        const assoc = Array.isArray(q.associated_services)
          ? q.associated_services
          : [];

        if (assoc.length > 0) {
          /* Schema-driven visibility */
          show = assoc.some((svc) => {
            const c = catFor(svc);
            return c ? !!cats[c] : false;
          });
        } else if (isVaccinationSection(q)) show = cats.boarding || cats.vet;
        else if (isAppearanceSection(q)) show = cats.grooming;
        else if (isBehaviourSection(q)) show = cats.grooming;
        else if (isMedSectionHeading(q)) show = cats.boarding || cats.vet;
        else if (isDietSection(q)) show = cats.boarding;
        else show = true;

        inMedBlock = isMedSectionHeading(q);
        inDietBlock = isDietSection(q);
      }

      if (!show) return;

      if (inMedBlock) {
        if (!isOnMedicationQ(q)) return;
      }
      if (inDietBlock) {
        if (!isMealTypeQ(q) && !isMealsPerDayQ(q)) return;
      }

      out.push(q);
    });
    return out;
  }, [petQuestions, serviceCategories]);

  const hasPetStep = petQuestions.length > 0;
  const stepKeys = useMemo(
    () => ["customer", ...(hasPetStep ? ["pet"] : [])],
    [hasPetStep],
  );

  const totalSteps = stepKeys.length;
  const stepKey = stepKeys[step];
  const isLastStep = step >= totalSteps - 1;

  useEffect(() => {
    if (!questions.length || !phone) return;
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      questions.forEach((q) => {
        if (isLockedPhoneQ(q)) {
          if (next[q._id] !== phone) {
            next[q._id] = phone;
            changed = true;
          }
        } else if (
          isWhatsappQ(q) &&
          TEXTY.includes((q.answerType || "").toLowerCase()) &&
          next[q._id] === undefined
        ) {
          next[q._id] = phone;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [questions, phone]);

    useEffect(() => {
      if (phase !== "form" || !schema || !phone || !vendorId) return;
      let cancelled = false;
      (async () => {
        const data = await getExistingSubmission(vendorId, formId, phone);
        if (cancelled) return;

              if (!data) {
                /* No prior submission for THIS phone → start fresh.
           Keep phone + WhatsApp pre-filled so the locked phone field
           still shows the number the user verified with. */
                setAnswers(() => {
                  const fresh = {};
                  questions.forEach((q) => {
                    if (isLockedPhoneQ(q)) {
                      fresh[q._id] = phone;
                    } else if (
                      isWhatsappQ(q) &&
                      TEXTY.includes((q.answerType || "").toLowerCase())
                    ) {
                      fresh[q._id] = phone;
                    }
                  });
                  return fresh;
                });
                setPetCount(1);
                setConsent({ agreed: false, signature: "" });
                setExistingSubmissionId(null);
                setAccountAlreadyCreated(false); 
                setCreateAccount(false); 
                return;
              }

        const { answers: hyd, consent: consentData } =
          hydrateAnswersFromSubmission({
            submission: data,
            customerQuestions,
            petQuestions,
            consentQ,
          });
        setAnswers(hyd);
        setPetCount(Math.max(1, data.pets?.length || 1));
        setConsent(consentData);
       setExistingSubmissionId(data._id || data.id || true);
      const wantsAccount = !!(
        data?.customer?.wants_account ??
        data?.customer?.wantsAccount ??
        data?.wants_account ??
        data?.wantsAccount
      );
      setAccountAlreadyCreated(wantsAccount);
      setCreateAccount(wantsAccount);
      })();
      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, schema, phone, vendorId, formId]);

  /* Resolve hydrated pet_type LABELS → _ids and load breeds/sizes */
  // const hydratedOnceRef = useRef(false);
  // useEffect(() => {
  //   if (hydratedOnceRef.current) return;
  //   if (!petTypes.length || !petQuestions.length) return;
  //   const typeQ = petQuestions.find(isPetTypeQ);
  //   if (!typeQ) {
  //     hydratedOnceRef.current = true;
  //     return;
  //   }
  //   let any = false;
  //   for (let i = 0; i < petCount; i++) {
  //     const key = petKey(typeQ._id, i);
  //     const v = answers[key];
  //     if (!v) continue;
  //     const isId = petTypes.some(
  //       (t) => String(t._id || t.id || t.flps_pet_type_id) === String(v),
  //     );
  //     if (isId) {
  //       handlePetTypeChange(i, String(v));
  //       any = true;
  //       continue;
  //     }
  //     const match = petTypes.find((t) =>
  //       [t.name, t.pet_type, t.petType, t.pet_type_name, t.title].some(
  //         (x) => x != null && norm(x) === norm(v),
  //       ),
  //     );
  //     if (!match) continue;
  //     const typeId = String(match._id || match.id || match.flps_pet_type_id);
  //     setAnswers((p) => ({ ...p, [key]: typeId }));
  //     handlePetTypeChange(i, typeId);
  //     any = true;
  //   }
  //   if (any) hydratedOnceRef.current = true;
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [petTypes, petQuestions, petCount]);

  // /* Resolve hydrated breed/size LABELS → _ids once options arrive */
  // useEffect(() => {
  //   if (!petQuestions.length) return;
  //   const breedQ = petQuestions.find(isBreedQ);
  //   const sizeQ = petQuestions.find(isSizeQ);
  //   const updates = {};
  //   for (let i = 0; i < petCount; i++) {
  //     const r = petRef[i] || {};
  //     const breeds = r.breeds || [];
  //     const sizes = r.sizes || [];

  //     if (breedQ && breeds.length) {
  //       const k = petKey(breedQ._id, i);
  //       const v = answers[k];
  //       if (v) {
  //         const has = breeds.some(
  //           (b) => String(b._id || b.id || b.flps_pet_breed_id) === String(v),
  //         );
  //         if (!has) {
  //           const m = breeds.find((b) =>
  //             [b.breed_name, b.name, b.breed, b.title, b.label].some(
  //               (x) => x != null && norm(x) === norm(v),
  //             ),
  //           );
  //           if (m) updates[k] = String(m._id || m.id || m.flps_pet_breed_id);
  //         }
  //       }
  //     }

  //     if (sizeQ && sizes.length) {
  //       const k = petKey(sizeQ._id, i);
  //       const v = answers[k];
  //       if (v) {
  //         const has = sizes.some(
  //           (s) =>
  //             String(
  //               s.flps_pet_size_id || s._id || s.id || s.size_label || s.name,
  //             ) === String(v),
  //         );
  //         if (!has) {
  //           const m = sizes.find((s) =>
  //             [
  //               s.size_label,
  //               s.name,
  //               s.size,
  //               s.size_name,
  //               s.pet_size,
  //               s.title,
  //             ].some((x) => x != null && norm(x) === norm(v)),
  //           );
  //           if (m)
  //             updates[k] = String(
  //               m.flps_pet_size_id || m._id || m.id || m.size_label || m.name,
  //             );
  //         }
  //       }
  //     }
  //   }
  //   if (Object.keys(updates).length) setAnswers((p) => ({ ...p, ...updates }));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [petRef, petQuestions, petCount]);
  /* Resolve hydrated pet_type / breed / size labels → _ids, load breeds+sizes ONCE per pet */
    const resolvedPetsRef = useRef({});
    useEffect(() => {
      if (!petTypes.length || !petQuestions.length) return;

      // reset cache whenever petTypes reference changes
      resolvedPetsRef.current = {};

      const typeQ = petQuestions.find(isPetTypeQ);
      const breedQ = petQuestions.find(isBreedQ);
      const sizeQ = petQuestions.find(isSizeQ);
      if (!typeQ) return;

      let cancelled = false;

      (async () => {
        for (let i = 0; i < petCount; i++) {
          const tKey = petKey(typeQ._id, i);
          const raw = answers[tKey];
          if (!raw) continue;

          const isId = petTypes.some(
            (t) => String(t._id || t.id || t.flps_pet_type_id) === String(raw),
          );
          let typeId = String(raw);
          if (!isId) {
            const m = petTypes.find((t) =>
              [t.name, t.pet_type, t.petType, t.pet_type_name, t.title].some(
                (x) => x != null && norm(x) === norm(raw),
              ),
            );
            if (!m) continue;
            typeId = String(m._id || m.id || m.flps_pet_type_id);
            if (cancelled) return;
            setAnswers((p) => ({ ...p, [tKey]: typeId }));
          }

          if (resolvedPetsRef.current[i] === typeId) continue;
          resolvedPetsRef.current[i] = typeId;

          const [breeds, sizes] = await Promise.all([
            getPetBreeds(typeId),
            getPetSizes(vendorId, typeId),
          ]);
          if (cancelled) return;

          setPetRef((p) => ({ ...p, [i]: { typeId, breeds, sizes } }));

          const updates = {};
          if (breedQ) {
            const bk = petKey(breedQ._id, i);
            const bv = answers[bk];
            if (
              bv &&
              !breeds.some(
                (b) =>
                  String(b._id || b.id || b.flps_pet_breed_id) === String(bv),
              )
            ) {
              const m = breeds.find((b) =>
                [b.breed_name, b.name, b.breed, b.title, b.label].some(
                  (x) => x != null && norm(x) === norm(bv),
                ),
              );
              if (m) updates[bk] = String(m._id || m.id || m.flps_pet_breed_id);
            }
          }
          if (sizeQ) {
            const sk = petKey(sizeQ._id, i);
            const sv = answers[sk];
            if (
              sv &&
              !sizes.some(
                (s) =>
                  String(
                    s.flps_pet_size_id ||
                      s._id ||
                      s.id ||
                      s.size_label ||
                      s.name,
                  ) === String(sv),
              )
            ) {
              const m = sizes.find((s) =>
                [
                  s.size_label,
                  s.name,
                  s.size,
                  s.size_name,
                  s.pet_size,
                  s.title,
                ].some((x) => x != null && norm(x) === norm(sv)),
              );
              if (m)
                updates[sk] = String(
                  m.flps_pet_size_id || m._id || m.id || m.size_label || m.name,
                );
            }
          }
          if (Object.keys(updates).length)
            setAnswers((p) => ({ ...p, ...updates }));
        }
      })();

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers, petTypes, petQuestions, petCount, vendorId]);

  useEffect(() => {
    if (!customerQuestions.length) return;
    const dobQ = customerQuestions.find(isDobQ);
    const ageQ = customerQuestions.find(isAgeQ);
    if (!dobQ || !ageQ) return;
    const dobVal = answers[dobQ._id];
    if (!dobVal) return;
    const computed = calcAge(dobVal);
    if (computed === null) return;
    const s = String(computed);
    if (answers[ageQ._id] !== s) setAnswers((p) => ({ ...p, [ageQ._id]: s }));
  }, [answers, customerQuestions]);

  useEffect(() => {
    if (!petQuestions.length || petCount <= 0) return;
    const dobQ = petQuestions.find(isDobQ);
    const ageQ = petQuestions.find(isAgeQ);
    if (!dobQ || !ageQ) return;
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (let i = 0; i < petCount; i++) {
        const dk = petKey(dobQ._id, i);
        const ak = petKey(ageQ._id, i);
        const dobVal = next[dk];
        if (!dobVal) continue;
        const computed = calcAge(dobVal);
        if (computed === null) continue;
        const s = String(computed);
        if (next[ak] !== s) {
          next[ak] = s;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [answers, petQuestions, petCount]);

  const loadForm = useCallback(async () => {
    setFormLoading(true);
    setFormErr(null);
    try {
      setSchema(await getFormById(vendorId, formId));
      return true;
    } catch (err) {
      setFormErr(err?.response?.data?.message || "Failed to load form");
      return false;
    } finally {
      setFormLoading(false);
    }
  }, [vendorId, formId]);

  const clearError = (key) =>
    setErrors((p) => {
      if (!p[key]) return p;
      const n = { ...p };
      delete n[key];
      return n;
    });

  const handlePetTypeChange = useCallback(
    (idx, typeId, keepAnswers = false) => {
      const staleKeys = petQuestions
        .filter((x) => isBreedQ(x) || isSizeQ(x))
        .map((x) => petKey(x._id, idx));
      if (!keepAnswers && staleKeys.length)
        setAnswers((p) => {
          const n = { ...p };
          staleKeys.forEach((k) => delete n[k]);
          return n;
        });
      setPetRef((p) => ({ ...p, [idx]: { typeId, breeds: [], sizes: [] } }));
      if (!typeId) return;
      Promise.all([getPetBreeds(typeId), getPetSizes(vendorId, typeId)]).then(
        ([breeds, sizes]) => {
          setPetRef((p) =>
            p[idx]?.typeId === typeId
              ? { ...p, [idx]: { typeId, breeds, sizes } }
              : p,
          );
        },
      );
    },
    [petQuestions, vendorId],
  );

  // useEffect(() => {
  //   if (!pendingPrefill || pendingPrefill.found === false) {
  //     if (pendingPrefill && pendingPrefill.found === false) setPendingPrefill(null);
  //     return;
  //   }
  //   if (!customerQuestions.length && !petQuestions.length) return;

  //   const h = hydrateAnswersFromSubmission({
  //     customerQuestions,
  //     petQuestions,
  //     consentQ,
  //     previous: pendingPrefill,
  //   });
  //   setPetCount(h.petCount);
  //   setConsent(h.consent);
  //   setAnswers((prev) => ({ ...prev, ...h.answers }));

  //   const typeQ = petQuestions.find(isPetTypeQ);
  //   if (typeQ) {
  //     for (let i = 0; i < h.petCount; i++) {
  //       const v = h.answers[`${typeQ._id}__${i}`];
  //       if (!v) continue;
  //       const match = petTypes.find((t) =>
  //         [
  //           t._id,
  //           t.id,
  //           t.flps_pet_type_id,
  //           t.name,
  //           t.pet_type,
  //           t.petType,
  //           t.pet_type_name,
  //           t.title,
  //         ].some((x) => x != null && String(x).toLowerCase() === String(v).toLowerCase()),
  //       );
  //       const typeId = match
  //         ? String(match._id || match.id || match.flps_pet_type_id)
  //         : "";
  //       if (typeId) handlePetTypeChange(i, typeId, { preserveAnswers: true });
  //     }
  //   }
  //   setPendingPrefill(null);
  // }, [
  //   pendingPrefill,
  //   customerQuestions,
  //   petQuestions,
  //   consentQ,
  //   petTypes,
  //   handlePetTypeChange,
  // ]);

  const setAnswer = (q, key, v, idx) => {
    setAnswers((p) => ({ ...p, [key]: v }));
    clearError(key);

    if (isPetTypeQ(q) && idx !== undefined) {
      if (!v) {
        handlePetTypeChange(idx, "");
        return;
      }
      const match = petTypes.find((t) =>
        [
          t._id,
          t.id,
          t.flps_pet_type_id,
          t.name,
          t.pet_type,
          t.petType,
          t.pet_type_name,
          t.title,
        ].some((x) => x != null && norm(x) === norm(v)),
      );
      const typeId = match
        ? String(match._id || match.id || match.flps_pet_type_id)
        : String(v);
      console.log("pet type picked:", v, "→ id:", typeId, "matched:", !!match);
      handlePetTypeChange(idx, typeId);
    }
  };
  const setRawAnswer = (key, v) => {
    setAnswers((p) => ({ ...p, [key]: v }));
    clearError(key);
  };

  const addPet = () => {
    if (petCount >= MAX_PETS) return;
    setPetCount(petCount + 1);
    setActivePet(petCount);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const removePet = (idx) => {
    if (petCount <= 1) return;
    setAnswers((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const m = k.match(/^(.*)__(\d+)(.*)$/);
        if (!m) {
          next[k] = prev[k];
          return;
        }
        const i = Number(m[2]);
        if (i === idx) return;
        next[i > idx ? `${m[1]}__${i - 1}${m[3]}` : k] = prev[k];
      });
      return next;
    });
    setPetRef((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const i = Number(k);
        if (i === idx) return;
        next[i > idx ? i - 1 : i] = prev[k];
      });
      return next;
    });
    setErrors({});
    setActivePet(() => {
      const next =
        activePet > idx
          ? activePet - 1
          : activePet === idx
            ? idx - 1
            : activePet;
      return Math.max(0, Math.min(next, petCount - 2));
    });
    setPetCount((c) => c - 1);
  };

  const applyLocation = ({ link, geo }, ownKey) => {
    const a = geo?.address || {};
    const vals = {
      country: a.country,
      state: a.state || a.state_district || a.region,
      city: a.city || a.town || a.village || a.municipality || a.county,
      pin: a.postcode,
      address: geo?.display_name,
    };
    const allowed = [
      "short_text",
      "text",
      "long_text",
      "textarea",
      "dropdown",
      "select",
      "single_choice",
      "radio",
    ];
    setAnswers((prev) => {
      const next = { ...prev, [ownKey]: link };
      questions.forEach((q) => {
        if (!allowed.includes((q.answerType || "").toLowerCase())) return;
        const t = qText(q);
        let v = null,
          onlyIfEmpty = false;
        if (/address/.test(t)) {
          v = vals.address;
          onlyIfEmpty = true;
        } else if (/\bcountry\b/.test(t)) v = vals.country;
        else if (/\bstate\b/.test(t)) v = vals.state;
        else if (/\b(city|town)\b/.test(t)) v = vals.city;
        else if (/(pin\s*code|pincode|zip|postal)/.test(t)) v = vals.pin;
        if (!v) return;
        if (onlyIfEmpty && !isEmpty(next[q._id])) return;
        const opts = normalizeOptions(q);
        if (opts.length) {
          const m =
            opts.find((o) => norm(o.label) === norm(v)) ||
            opts.find(
              (o) =>
                norm(o.label).includes(norm(v)) ||
                norm(v).includes(norm(o.label)),
            );
          if (m) next[q._id] = m.value;
        } else next[q._id] = v;
      });
      return next;
    });
    setErrors({});
  };

  const collectErrors = (qs, multi) => {
    const errs = {};
    const dobQ0 = qs.find(isDobQ);
    const ageQ0 = qs.find(isAgeQ);
    const otherFilled = (q, i) => {
      if (!dobQ0 || !ageQ0) return false;
      const other = isDobQ(q) ? ageQ0 : isAgeQ(q) ? dobQ0 : null;
      if (!other) return false;
      return !isEmpty(answers[multi ? petKey(other._id, i) : other._id]);
    };
        qs.forEach((q) => {
          if (NO_ANSWER.includes(q.answerType) || !q.isRequired) return;
          if (multi)
            for (let i = 0; i < petCount; i++) {
              const k = petKey(q._id, i);

              /* vaccination answers live inside the vacc__i__items repeater,
             not under petKey, so check the repeater instead */
              if (/vaccin/.test(qText(q))) {
                const rows = answers[`vacc__${i}__items`];
                const inRepeater =
                  Array.isArray(rows) && rows.some((r) => !isEmpty(r?.[q._id]));
                if (inRepeater || existingSubmissionId) continue;
              }

              if (isEmpty(answers[k]) && !otherFilled(q, i))
                errs[k] = "This field is required";
            }
          else if (isEmpty(answers[q._id]) && !otherFilled(q, 0))
            errs[q._id] = "This field is required";
        });
    return errs;
  };
  const scrollToKey = (key) =>
    setTimeout(
      () =>
        document
          .getElementById(`q-${key}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" }),
      80,
    );

  const focusError = (errs) => {
    const first = Object.keys(errs)[0];
    const m = first.match(/__(\d+)$/);
    if (m) setActivePet(Number(m[1]));
    scrollToKey(first);
  };

  const goContinue = () => {
    if (busyCount > 0) {
      setFormErr("Please wait for your uploads to finish.");
      return false;
    }
    setFormErr(null);
    const qs =
      stepKey === "customer"
        ? customerQuestions
        : stepKey === "pet"
          ? visiblePetQuestions // ← changed
          : [];
    const errs = collectErrors(qs, stepKey === "pet");
    if (Object.keys(errs).length) {
      setErrors((p) => ({ ...p, ...errs }));
      focusError(errs);
      return false;
    }
    setStep((s) => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
    return true;
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (busyCount > 0) {
      setSubmitErr("Please wait for your uploads to finish.");
      return;
    }
    const allErrs = {};
    let firstBad = -1;
    const cErr = collectErrors(customerQuestions, false);
    if (Object.keys(cErr).length) {
      firstBad = 0;
      Object.assign(allErrs, cErr);
    }
    if (hasPetStep) {
      const pErr = collectErrors(visiblePetQuestions, true);
      if (Object.keys(pErr).length && firstBad === -1)
        firstBad = stepKeys.indexOf("pet");
      Object.assign(allErrs, pErr);
    }
       if (firstBad !== -1) {
         console.log("BLOCKED BY VALIDATION:", allErrs);
         Object.keys(allErrs).forEach((k) => {
           const qid = k.split("__")[0];
           const found = [...customerQuestions, ...visiblePetQuestions].find(
             (q) => q._id === qid,
           );
           console.log("Missing:", found?.question, "| key:", k);
         });
         setErrors(allErrs);
         setStep(firstBad);
         setSubmitErr("Some required fields are empty. Please check the form.");
         focusError(allErrs);
         return;
       }
    setSubmitting(true);
    setSubmitErr(null);
    try {
      const payload = buildSubmitPayload({
        customerQuestions,
        petQuestions,
        consentQ,
        answers,
        petCount,
        phone,
        petTypes,
        petRef,
        consent,
        schema,
        createAccount,
      });
            await submitForm(vendorId, {
              ...payload,
              form_id: formId,
              submission_id: existingSubmissionId || undefined,
            });
      setSubmitted(true);
    } catch (err) {
      setSubmitErr(
        err?.response?.data?.message || "Submission failed. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const petCtxFor = (idx) => {
    const r = petRef[idx] || {};
    let typeId = r.typeId || "";

    /* Fallback: if petRef hasn't been populated yet (e.g. right after hydrate),
     resolve the typeId from `answers` directly so breed/size aren't hidden. */
    if (!typeId) {
      const typeQ = petQuestions.find(isPetTypeQ);
      if (typeQ) {
        const raw = answers[petKey(typeQ._id, idx)];
        if (raw) {
          const isId = petTypes.some(
            (t) => String(t._id || t.id || t.flps_pet_type_id) === String(raw),
          );
          if (isId) {
            typeId = String(raw);
          } else {
            const m = petTypes.find((t) =>
              [t.name, t.pet_type, t.petType, t.pet_type_name, t.title].some(
                (x) => x != null && norm(x) === norm(raw),
              ),
            );
            if (m) typeId = String(m._id || m.id || m.flps_pet_type_id);
          }
        }
      }
    }

    return {
      types: petTypes,
      breeds: r.breeds || [],
      sizes: r.sizes || [],
      selectedPetTypeId: typeId,
    };
  };

  const isAgeLockedFor = useCallback(
    (fieldKey, q) => {
      if (!isAgeQ(q)) return false;
      const m = fieldKey.match(/^(.*)__(\d+)$/);
      if (m) {
        const idx = m[2];
        const dobQ = petQuestions.find(isDobQ);
        if (!dobQ) return false;
        return !!answers[petKey(dobQ._id, idx)];
      }
      const dobQ = customerQuestions.find(isDobQ);
      if (!dobQ) return false;
      return !!answers[dobQ._id];
    },
    [answers, petQuestions, customerQuestions],
  );

  const dobQ = questions.find(isDobQ);
  const dobFilled = !!(dobQ && answers[dobQ._id]);

  return (
    <FormCtx.Provider
      value={{
        vendorId,
        formId,
        phase,
        setPhase,
        step,
        setStep,
        totalSteps,
        hasPetStep,
        isLastStep,
        phone,
        setPhone,
        vendor,
        schema,
        questions,
        customerQuestions,
        petQuestions,
        consentQ,
        formLoading,
        formErr,
        loadForm,
        answers,
        errors,
        setAnswer,
        setRawAnswer,
        clearError,
        setErrors,
        applyLocation,
        scrollToKey,
        goContinue,
        goBack,
        submitting,
        submitted,
        existingSubmissionId,
        createAccount,
        setCreateAccount,
        accountAlreadyCreated,
        handleSubmit,
        petTypes,
        petCount,
        addPet,
        removePet,
        petCtxFor,
        sendOtp,
        verifyOtp,
        dobFilled,
        isAgeLockedFor,
        stepKeys,
        stepKey,
        activePet,
        setActivePet,
        consent,
        setConsent,
        consentErr,
        setConsentErr,
        submitErr,
        serviceCategories,
        busyCount,
        onBusy,
        uploadPublicFile,
        visiblePetQuestions,
      }}
    >
      {children}
    </FormCtx.Provider>
  );
}

/* ---------- SHARED ---------- */
export const STEP_LABELS = {
  customer: "Pet Parent",
  pet: "Pet",
};

export function Stepper({ keys, active }) {
  return (
    <ol className="ff-steps">
      {keys.map((k, i) => (
        <li
          key={k}
          className={`ff-step${i === active ? " is-active" : ""}${i < active ? " is-done" : ""}`}
        >
          <span className="ff-step-dot">{i < active ? "✓" : i + 1}</span>
          <span className="ff-step-label">{STEP_LABELS[k]}</span>
        </li>
      ))}
    </ol>
  );
}

export function OtpInput({ value, onChange, length = 4, disabled, autoFocus }) {
  const refs = useRef([]);
  useEffect(() => {
    if (autoFocus && !disabled && refs.current[0]) refs.current[0].focus();
  }, [autoFocus, disabled]);
  const handleChange = (i, raw) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const arr = value.split("");
    arr[i] = digit;
    onChange(arr.join("").slice(0, length));
    if (digit && i < length - 1 && refs.current[i + 1])
      refs.current[i + 1].focus();
  };
  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !value[i] && i > 0)
      refs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < length - 1) refs.current[i + 1]?.focus();
  };
  const handlePaste = (e) => {
    const p = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);
    if (p) {
      e.preventDefault();
      onChange(p);
      refs.current[Math.min(p.length, length - 1)]?.focus();
    }
  };
  return (
    <div style={styles.otpWrap} onPaste={handlePaste}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          value={value[i] || ""}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          style={{ ...styles.otpBox, opacity: disabled ? 0.5 : 1 }}
        />
      ))}
    </div>
  );
}

/* ----------------------------------------------------------
   UploadBox — now uses S3-backed file descriptors:
     { name, url, key, type, size, category }
---------------------------------------------------------- */
export function UploadBox({
  id,
  accept,
  multiple,
  items,
  onPick,
  onRemove,
  busy,
  error,
  isImage,
  compact,
  noRemove,
}) {
 const inputId = `${id}--file`;
 const isImg = (f) =>
   (f?.mime_type || f?.type || "").toLowerCase().startsWith("image/");
 const [drag, setDrag] = useState(false);
  const [sizeErr, setSizeErr] = useState("");

  const take = (list) => {
    if (busy) return;
    const arr = Array.from(list || []);
    if (!arr.length) return;
    const big = arr.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (big) {
      setSizeErr(`"${big.name}" is larger than ${MAX_FILE_MB} MB`);
      return;
    }
    if (isImage && arr.some((f) => !f.type.startsWith("image/"))) {
      setSizeErr("Please choose an image file");
      return;
    }
    setSizeErr("");
    onPick(multiple ? arr : arr.slice(0, 1));
  };

  const has = items && items.length > 0;
  const hideRemove = (f, i) =>
    typeof noRemove === "function" ? noRemove(f, i) : !!noRemove;

  if (compact && has) {
    return (
      <div>
        <input
          id={inputId}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={busy}
          onChange={(e) => {
            take(e.target.files);
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((f, i) => (
            <div
              key={`${f.key || f.name}-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                border: "1px solid #E5E7EB",
                borderRadius: 8,
                background: "#fff",
              }}
            >
              {isImg(f) ? (
                <img
                  src={f.url}
                  alt={f.name}
                  style={{
                    width: 40,
                    height: 40,
                    objectFit: "cover",
                    borderRadius: 6,
                  }}
                />
              ) : (
                <span style={{ fontSize: 16 }}>📄</span>
              )}
              <span
                style={{
                  flex: 1,
                  fontSize: 14,
                  color: "#111827",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={f.name}
              >
                {f.name}
              </span>
              {hideRemove(f, i) ? (
                <span
                  title="Saved — can't be removed"
                  style={{
                    fontSize: 14,
                    color: "#059669",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  ✓
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  title="Remove"
                  style={{
                    background: "none",
                    border: 0,
                    color: "#EF4444",
                    fontSize: 20,
                    fontWeight: 700,
                    cursor: "pointer",
                    padding: 0,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        {/* ↓↓↓ NEW — this is what lets you add more files ↓↓↓ */}
        <label
          htmlFor={inputId}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginTop: 8,
            padding: "8px 14px",
            border: "1px dashed #EA580C",
            borderRadius: 8,
            background: "#fff",
            color: "#EA580C",
            fontSize: 13,
            fontWeight: 600,
            cursor: busy ? "progress" : "pointer",
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? "Uploading…" : "+ Add more"}
        </label>

        {sizeErr && (
          <div style={{ fontSize: 12, color: "#EF4444", marginTop: 6 }}>
            {sizeErr}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <input
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={busy}
        onChange={(e) => {
          take(e.target.files);
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />
      <label
        htmlFor={inputId}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          take(e.dataTransfer.files);
        }}
        style={{
          ...styles.uploadBox,
          ...(compact
            ? {
                minHeight: 56,
                padding: "12px 16px",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 12,
              }
            : {}),
          background: drag ? "#F3F4F6" : "#FAFAFA",
          borderColor: error ? "#EF4444" : drag ? "#3B82F6" : "#D1D5DB",
          cursor: busy ? "progress" : "pointer",
        }}
      >
        {busy ? (
          <>
            <div style={styles.uploadSpinner} />
            <span style={styles.uploadTitle}>Uploading…</span>
            <span style={styles.uploadSub}>Please wait</span>
          </>
        ) : compact ? (
          <>
            <span style={{ fontSize: 20 }}>{isImage ? "🖼️" : "📎"}</span>
            <span style={{ fontSize: 14, color: "#4B5563" }}>
              Drop {isImage ? "image" : "file"} here, or{" "}
              <span style={styles.uploadBrowse}>browse</span>
            </span>
          </>
        ) : (
          <>
            <div style={styles.uploadIcon}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect
                  x="6"
                  y="6"
                  width="36"
                  height="36"
                  rx="6"
                  fill="#E5E7EB"
                />
                <circle cx="17" cy="18" r="3.5" fill="#9CA3AF" />
                <path
                  d="M12 38 L22 26 L30 33 L36 27 L42 34 L42 38 Z"
                  fill="#6B7280"
                />
              </svg>
            </div>
            <span style={styles.uploadTitle}>
              Drop your {isImage ? "image" : "file"} here, or{" "}
              <span style={styles.uploadBrowse}>browse</span>
            </span>
            <span style={styles.uploadSub}>
              Supports: {isImage ? "JPG, JPEG2000, PNG" : "PDF, DOC, DOCX"} ·
              max {MAX_FILE_MB} MB
            </span>
          </>
        )}
      </label>
      {sizeErr && (
        <div style={{ fontSize: 12, color: "#EF4444", marginTop: 6 }}>
          {sizeErr}
        </div>
      )}

      {has && (
        <div style={styles.fileGrid}>
          {items.map((f, i) => (
            <div key={`${f.key || f.name}-${i}`} style={styles.fileItem}>
              {isImg(f) ? (
                <img src={f.url} alt={f.name} style={styles.thumb} />
              ) : (
                <div style={styles.fileIcon}>📄</div>
              )}
              <span style={styles.fileItemName}>{f.name}</span>
              <span style={styles.fileOk}>✓ Uploaded</span>
              {!hideRemove(f, i) && (
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  style={styles.fileRemove}
                  aria-label={`Remove ${f.name}`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SignaturePad({ onChange }) {
  const canvasRef = useRef(null),
    drawing = useRef(false),
    cbRef = useRef(onChange);
  cbRef.current = onChange;
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    const getPos = (e) => {
      const r = c.getBoundingClientRect();
      const cx = e.touches?.[0]?.clientX ?? e.clientX,
        cy = e.touches?.[0]?.clientY ?? e.clientY;
      return {
        x: (cx - r.left) * (c.width / r.width),
        y: (cy - r.top) * (c.height / r.height),
      };
    };
    const start = (e) => {
      e.preventDefault();
      drawing.current = true;
      const { x, y } = getPos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (e) => {
      if (!drawing.current) return;
      e.preventDefault();
      const { x, y } = getPos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    };
    const end = (e) => {
      if (!drawing.current) return;
      e.preventDefault();
      drawing.current = false;
      cbRef.current(c.toDataURL("image/png"));
    };
    c.addEventListener("mousedown", start);
    c.addEventListener("mousemove", move);
    c.addEventListener("mouseup", end);
    c.addEventListener("mouseleave", end);
    c.addEventListener("touchstart", start, { passive: false });
    c.addEventListener("touchmove", move, { passive: false });
    c.addEventListener("touchend", end, { passive: false });
    return () => {
      c.removeEventListener("mousedown", start);
      c.removeEventListener("mousemove", move);
      c.removeEventListener("mouseup", end);
      c.removeEventListener("mouseleave", end);
      c.removeEventListener("touchstart", start);
      c.removeEventListener("touchmove", move);
      c.removeEventListener("touchend", end);
    };
  }, []);
  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    cbRef.current("");
  };
  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        style={styles.signatureCanvas}
      />
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}
      >
        <button type="button" onClick={clear} style={styles.clearBtn}>
          Clear signature
        </button>
      </div>
    </div>
  );
}

/* ---------- CREATE ACCOUNT TOGGLE ---------- */
export function CreateAccountToggle() {
  const {
    createAccount,
    setCreateAccount,
    phone,
    accountAlreadyCreated,
  } = useForm();

  const locked = !!accountAlreadyCreated; // ← once true, box is locked ON

  return (
    <div className="ff-field ff-full" style={styles.createAccountBox}>
      <label
        style={{
          ...styles.createAccountLabel,
          cursor: locked ? "not-allowed" : "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={locked ? true : createAccount}
          disabled={locked}
          onChange={(e) => {
            if (locked) return;          // ← block uncheck
            setCreateAccount(e.target.checked);
          }}
          style={{
            accentColor: "#EA580C",
            width: 18,
            height: 18,
            marginTop: 2,
            flexShrink: 0,
            cursor: locked ? "not-allowed" : "pointer",
            opacity: locked ? 0.7 : 1,
          }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={styles.createAccountTitle}>
            Create my account on FindLocalPetServices
          </div>

          {locked ? (
            /* ── Account already created → locked message ── */
            <p style={styles.createAccountDesc}>
               Account already created with the verified phone number{" "}
              <b>{phone}</b> on{" "}
              <a
                href="https://findlocalpetservices.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  color: "#EA580C",
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
              >
                FindLocalPetServices
              </a>
              .
            </p>
          ) : (
            /* ── Not created yet → normal prompt ── */
            <p style={styles.createAccountDesc}>
              If you tick this box, a customer account will be created with your
              verified phone number <b>{phone}</b> on{" "}
              <a
                href="https://findlocalpetservices.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  color: "#EA580C",
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
              >
                FindLocalPetServices
              </a>
              .
            </p>
          )}
        </div>
      </label>
    </div>
  );
}

const FULL_TYPES = new Set([
  "long_text",
  "textarea",
  "signature",
  "image_upload",
  "file_upload",
  "multi_file_upload",
  "description_text",
  "single_choice",
  "radio",
  "multiple_choice",
  "multiselect",
  "checkbox",
  "url",
  "location",
]);

const FILE_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif";

/* ---------- SEARCHABLE DROPDOWN (FOR BREED ONLY) ---------- */
function SearchableSelect({ id, value, options, onChange, placeholder, disabled, style }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const filteredOptions = options.filter((o) =>
    String(o.label).toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = options.find((o) => String(o.value) === String(value))?.label || "";

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          ...style,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          background: disabled ? "#F9FAFB" : "#fff",
          opacity: disabled ? 0.7 : 1,
          minHeight: 44,
        }}
      >
        <span style={{ color: value ? "#111827" : "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedLabel || placeholder}
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginLeft: 8 }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      {isOpen && !disabled && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
          background: "#fff", border: "1px solid #D1D5DB", borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)", marginTop: 4,
          display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: 8, borderBottom: "1px solid #E5E7EB" }}>
            <input
              autoFocus
              type="text"
              placeholder="Search breed..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 6,
                border: "1px solid #D1D5DB", outline: "none", fontSize: 16, 
              }}
            />
          </div>
          <div style={{ overflowY: "auto", maxHeight: 220 }}>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: 12, textAlign: "center", fontSize: 13, color: "#6B7280" }}>
                No breeds found
              </div>
            ) : (
              filteredOptions.map((o, i) => (
                <div
                  key={`${o.value}-${i}`}
                  onClick={() => {
                    onChange(o.value);
                    setIsOpen(false);
                    setSearch("");
                  }}
                  style={{
                    padding: "12px 14px", cursor: "pointer", fontSize: 14,
                    background: value === o.value ? "#F0FDFA" : "transparent",
                    color: value === o.value ? "#EA580C" : "#111827",
                    borderBottom: "1px solid #F3F4F6",
                  }}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- QUESTION FIELD (S3-backed uploads) ---------- */
export function QuestionField({
  q,
  fieldKey,
  value,
  error,
  onChange,
  onLocate,
  petCtx,
  readonly,
}) {
   const {
     isAgeLockedFor,
     vendorId,
     onBusy,
     setPhase,
     setPhone,
     existingSubmissionId,
   } = useForm();
  const [locLoading, setLocLoading] = useState(false);
  const [locErr, setLocErr] = useState(null);
  const [locInfo, setLocInfo] = useState("");
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState("");

  const id = `q-${fieldKey}`;
  let meta = {};
  try {
    meta = JSON.parse(q.fieldMeta || "{}");
  } catch {}
  const lowerQ = qText(q);
  const rawType = (q.answerType || "short_text").toLowerCase();
  let type = rawType;
  const inputStyle = {
    ...styles.input,
    borderColor: error ? "#EF4444" : "#D1D5DB",
  };
  const locked = isLockedPhoneQ(q);
  const whatsapp = isWhatsappQ(q) && TEXTY.includes(rawType);
  const isAge = isAgeQ(q);
  if (locked || whatsapp) type = "phone";

  const rawOptions = normalizeOptions(q);
  const { types, breeds, sizes, selectedPetTypeId } = petCtx || {};
  const isPetType = isPetTypeQ(q),
    isBreed = isBreedQ(q),
    isGender = isGenderQ(q);
  const isSize =
    isSizeQ(q) &&
    ["dropdown", "select", "single_choice", "radio"].includes(rawType);
  // const isMealType = isMealTypeQ(q);
  if (petCtx && (isBreed || isSizeQ(q)) && !selectedPetTypeId) return null;

  let options = rawOptions,
    disabledHint = "";
  if (rawOptions.length === 0) {
    if (isPetType) {
      const iconFor = (label) => {
        const l = String(label).toLowerCase();
        if (/cat|kitten|feline/.test(l)) return "🐱";
        if (/dog|puppy|canine/.test(l)) return "🐶";
        return "🐾";
      };

      if ((types || []).length > 0) {
        // API is the source of truth
        options = types
          .filter((t) => {
            const label = (
              t.name ||
              t.pet_type ||
              t.petType ||
              t.pet_type_name ||
              t.title ||
              ""
            ).toLowerCase();
            return /cat|dog/.test(label);
          })
          .map((t) => {
            const raw =
              t.name ||
              t.pet_type ||
              t.petType ||
              t.pet_type_name ||
              t.title ||
              "Unnamed";
            return {
              label: `${iconFor(raw)}  ${raw}`,
              value: t._id || t.id || t.flps_pet_type_id || t.name,
            };
          });
      } else {
        // fallback: keep schema options but still add icons
        options = rawOptions.map((o) => ({
          ...o,
          label: `${iconFor(o.label)}  ${o.label}`,
        }));
      }

      if (
        [
          "dropdown",
          "select",
          "single_choice",
          "radio",
          "short_text",
          "text",
        ].includes(rawType)
      ) {
        type = "single_choice";
      }
    } else if (isBreed) {
      options = (breeds || []).map((b) => ({
        label:
          b.breed_name || b.name || b.breed || b.title || b.label || "Unnamed",
        value: b._id || b.id || b.flps_pet_breed_id || b.name,
      }));
      if (!selectedPetTypeId) disabledHint = "Select pet type first";
    } else if (isSize) {
      options = (sizes || []).map((s) => ({
        label:
          s.size_label ||
          s.name ||
          s.size ||
          s.size_name ||
          s.pet_size ||
          s.title ||
          "Unnamed",
        value: s.flps_pet_size_id || s._id || s.id || s.size_label || s.name,
      }));
      if (!selectedPetTypeId) disabledHint = "Select pet type first";
      else if (options.length === 0) disabledHint = "No sizes available";
    } else if (isGender)
      options = [
        { label: "Male", value: "Male" },
        { label: "Female", value: "Female" },
      ];
  }
  /* ===== API data wins over options saved in the form schema ===== */
  if (isPetType && (types || []).length > 0) {
    const iconFor = (label) => {
      const l = String(label).toLowerCase();
      if (/cat|kitten|feline/.test(l)) return "🐱";
      if (/dog|puppy|canine/.test(l)) return "🐶";
      return "🐾";
    };
    const nameOf = (t) =>
      t.name ||
      t.pet_type ||
      t.petType ||
      t.pet_type_name ||
      t.title ||
      "Unnamed";

    options = types
      .filter((t) => /cat|dog/i.test(nameOf(t)))
      .sort((a, b) => (/cat/i.test(nameOf(a)) ? -1 : 1)) // Cat first
      .map((t) => ({
        label: `${iconFor(nameOf(t))}  ${nameOf(t)}`,
        value: t._id || t.id || t.flps_pet_type_id || t.name,
      }));
    type = "single_choice";
  }

  if (petCtx && selectedPetTypeId) {
    if (isBreed && (breeds || []).length > 0) {
      options = breeds.map((b) => ({
        label:
          b.breed_name || b.name || b.breed || b.title || b.label || "Unnamed",
        value: b._id || b.id || b.flps_pet_breed_id || b.name,
      }));
      type = "dropdown";
      disabledHint = "";
    } else if (isSizeQ(q) && (sizes || []).length > 0) {
      options = sizes.map((s) => ({
        label:
          s.size_label ||
          s.name ||
          s.size ||
          s.size_name ||
          s.pet_size ||
          s.title ||
          "Unnamed",
        value: s.flps_pet_size_id || s._id || s.id || s.size_label || s.name,
      }));
      type = "dropdown";
      disabledHint = "";
    }
  }
  if (
    options.length === 0 &&
    !disabledHint &&
    (rawType === "single_choice" || rawType === "radio")
  ) {
    options = [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ];
  }
  if (TEXTY.slice(0, 2).includes(rawType) && !locked && !whatsapp) {
    if (isPetType && options.length > 0) type = "single_choice";
    else if (isGender) type = "dropdown";
    else if (isBreed && (!selectedPetTypeId || options.length > 0))
      type = "dropdown";
  }
  /* Meals Per Day → force a 1..6 dropdown */
  if (isMealsPerDayQ(q)) {
    type = "dropdown";
    options = [1, 2, 3, 4, 5, 6].map((n) => ({
      label: String(n),
      value: String(n),
    }));
    disabledHint = "";
  }

  const isMapsField =
    (type === "url" && /(map|location)/.test(lowerQ)) || type === "location";
  const showLabel = !NO_ANSWER.includes(type) && type !== "checkbox";
  const label = showLabel ? (
    <label style={styles.label} htmlFor={id}>
      {q.question} {q.isRequired && <span style={{ color: "#EF4444" }}>*</span>}
    </label>
  ) : null;

  const shownError = error || upErr;
  const errEl = shownError && (
    <div style={{ fontSize: 12, color: "#EF4444", marginTop: 6 }}>
      {shownError}
    </div>
  );

  /* ── S3 upload handler ── */
  const itemsOf = (v) => asList(v).filter(isFileVal);

  const handlePick = async (files, multiple) => {
    setUpErr("");
    setUploading(true);
    onBusy(1);
    try {
      const category = docTypeFor(q.question);
      const done = [];
      for (const f of files) {
        done.push(await uploadPublicFile(vendorId, f, category));
      }
      if (multiple) onChange([...itemsOf(value), ...done]);
      else onChange(done[0] || null);
    } catch (e) {
      setUpErr(
        e?.response?.data?.message || "Upload failed. Please try again.",
      );
    } finally {
      setUploading(false);
      onBusy(-1);
    }
  };

  const locate = () => {
    if (!navigator.geolocation) {
      setLocErr("Location is not supported");
      return;
    }
    setLocLoading(true);
    setLocErr(null);
    setLocInfo("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const link = `https://www.google.com/maps?q=${latitude},${longitude}`;
        let geo = null;
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&accept-language=en&lat=${latitude}&lon=${longitude}`,
          );
          if (r.ok) geo = await r.json();
        } catch {}
        onLocate({ link, geo });
        const a = geo?.address || {};
        const parts = [
          a.city || a.town || a.village || a.county,
          a.state,
          a.country,
        ].filter(Boolean);
        setLocInfo(parts.join(", "));
        setLocLoading(false);
      },
      (err) => {
        setLocErr(
          err.code === 1
            ? "Permission denied."
            : err.code === 3
              ? "Timed out."
              : "Could not get location.",
        );
        setLocLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  let control;
  switch (type) {
    case "short_text":
    case "text":
      control = (
        <input
          id={id}
          type="text"
          placeholder={meta.placeholder || ""}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
      break;
    case "long_text":
    case "textarea":
      control = (
        <textarea
          id={id}
          rows={2}
          placeholder={meta.placeholder || ""}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, resize: "vertical", minHeight: 52 }}
        />
      );
      break;
    case "number":
      control = (
        <input
          id={id}
          type="number"
          placeholder={meta.placeholder || ""}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
      break;
    case "phone":
      control = (
        <div>
          <input
            id={id}
            type="tel"
            value={value || ""}
            readOnly={locked}
            maxLength={locked ? 10 : 15}
            onChange={(e) =>
              !locked && onChange(e.target.value.replace(/[^\d+]/g, ""))
            }
            style={{
              ...inputStyle,
              ...(locked
                ? {
                    cursor: "not-allowed",
                    /* keep normal background + dark text so the phone
                       number is clearly visible */
                  }
                : {}),
            }}
          />
          {locked && (
            <div
              style={{
                ...styles.hintGreen,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <span>🔒 Verified with OTP</span>

              {!existingSubmissionId && (
                <button
                  type="button"
                  onClick={() => {
                    /* clear phone so step 1 asks for a fresh number */
                    setPhone("");
                    /* back to OTP verification */
                    setPhase("auth");
                  }}
                  style={{
                    background: "none",
                    border: 0,
                    padding: 0,
                    color: "#EA580C",
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Need to change this?
                </button>
              )}
            </div>
          )}
          {whatsapp && (
            <div style={styles.hintGrey}>
              Filled from your verified number. You can change it.
            </div>
          )}
        </div>
      );
      break;
    case "email":
      control = (
        <input
          id={id}
          type="email"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
      break;
    case "url":
    case "location":
      control = (
        <div>
          <input
            id={id}
            type="url"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…"
            style={inputStyle}
          />
          {isMapsField && (
            <>
              <button
                type="button"
                onClick={locate}
                disabled={locLoading}
                style={{ ...styles.locationBtn, opacity: locLoading ? 0.6 : 1 }}
              >
                {locLoading ? "Getting location…" : "📍 Use current location"}
              </button>
              {locInfo && <div style={styles.hintGreen}>✓ {locInfo}</div>}
              {locErr && (
                <div style={{ fontSize: 12, color: "#EF4444", marginTop: 6 }}>
                  {locErr}
                </div>
              )}
            </>
          )}
        </div>
      );
      break;
    case "date_picker":
    case "date": {
      const today = new Date();
      const maxDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      control = (
        <input
          id={id}
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
          max={isDobQ(q) ? maxDate : undefined}
        />
      );
      break;
    }
    case "time_picker":
    case "time":
      control = (
        <input
          id={id}
          type="time"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
      break;
    case "yes_no":
      control = (
        <div style={styles.radioRow}>
          {["Yes", "No"].map((o) => (
            <label
              key={o}
              style={{
                ...styles.pillChoice,
                ...(value === o ? styles.pillChoiceOn : {}),
              }}
            >
              <input
                type="radio"
                name={id}
                checked={value === o}
                onChange={() => onChange(o)}
                style={{ accentColor: "#EA580C" }}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
      break;
    case "dropdown":
    case "select":
      if (isBreed) {
        // ✅ ONLY Breed uses the new searchable dropdown
        control = (
          <SearchableSelect
            id={id}
            value={value}
            options={options}
            onChange={onChange}
            placeholder={disabledHint || "Select Breed…"}
            disabled={!!disabledHint}
            style={inputStyle}
          />
        );
      } else {
        // ✅ Pet Type, Gender, Meals per day, etc. keep the original dropdown
        control = (
          <select
            id={id}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={!!disabledHint}
            style={{
              ...inputStyle,
              background: disabledHint ? "#F9FAFB" : "#fff",
            }}
          >
            <option value="">{disabledHint || "Select…"}</option>
            {options.map((o, i) => (
              <option key={`${o.value}-${i}`} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        );
      }
      break;
    case "select":
      control = (
        <select
          id={id}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={!!disabledHint}
          style={{
            ...inputStyle,
            background: disabledHint ? "#F9FAFB" : "#fff",
          }}
        >
          <option value="">{disabledHint || "Select…"}</option>
          {options.map((o, i) => (
            <option key={`${o.value}-${i}`} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
      break;
    case "single_choice":
    case "radio":
      control = (
        <div style={styles.radioRow}>
          {disabledHint ? (
            <div style={styles.hintGrey}>{disabledHint}</div>
          ) : options.length === 0 ? (
            <span style={{ fontSize: 13, color: "#9CA3AF" }}>
              No options available
            </span>
          ) : (
            options.map((o, i) => {
              const checked = value === o.value;
              return (
                <label
                  key={`${o.value}-${i}`}
                  style={{
                    ...styles.pillChoice,
                    ...(checked ? styles.pillChoiceOn : {}),
                  }}
                >
                  <input
                    type="radio"
                    name={id}
                    checked={checked}
                    onChange={() => onChange(o.value)}
                    style={{ accentColor: "#EA580C" }}
                  />
                  <span>{o.label}</span>
                </label>
              );
            })
          )}
        </div>
      );
      break;
    case "multiple_choice":
    case "multiselect": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (v) =>
        onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
      const colLayout = isServiceInterestQ(q);
      const wrapStyle = colLayout
        ? {
            display: "flex",
            flexDirection: "column",
            flexWrap: "nowrap",
            gap: 8,
            alignItems: "stretch",
          }
        : styles.radioRow;
      control = (
        <div style={wrapStyle}>
          {disabledHint ? (
            <div style={styles.hintGrey}>{disabledHint}</div>
          ) : (
            options.map((o, i) => {
              const checked = arr.includes(o.value);
              return (
                <label
                  key={`${o.value}-${i}`}
                  style={{
                    ...styles.pillChoice,
                    ...(checked ? styles.pillChoiceOn : {}),
                    ...(colLayout
                      ? {
                          display: "flex",
                          width: "100%",
                          boxSizing: "border-box",
                        }
                      : {}),
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(o.value)}
                    style={{ accentColor: "#EA580C" }}
                  />
                  <span>{o.label}</span>
                </label>
              );
            })
          )}
        </div>
      );
      break;
    }
    case "checkbox": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (v) => onChange(arr.includes(v) ? [] : [v]);
      control = (
        <div style={styles.radioRow}>
          {options.map((o, i) => {
            const checked = arr.includes(o.value);
            return (
              <label
                key={`${o.value}-${i}`}
                style={{
                  ...styles.pillChoice,
                  ...(checked ? styles.pillChoiceOn : {}),
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(o.value)}
                  style={{ accentColor: "#EA580C" }}
                />
                <span>{o.label}</span>
              </label>
            );
          })}
        </div>
      );
      break;
    }
    case "image_upload":
      control = (
        <UploadBox
          id={id}
          accept="image/*"
          isImage
          compact
          items={itemsOf(value)}
          busy={uploading}
          error={shownError}
          onPick={(files) => handlePick(files, false)}
          onRemove={() => onChange(null)}
          noRemove={(f) => !!f?._saved}
        />
      );
      break;
    case "file_upload":
      control = (
        <UploadBox
          id={id}
          accept={FILE_ACCEPT}
          multiple
          compact
          noRemove={(f) => !!f?._saved}
          items={itemsOf(value)}
          busy={uploading}
          error={shownError}
          onPick={(files) => handlePick(files, true)}
          onRemove={(i) => onChange(itemsOf(value).filter((_, k) => k !== i))}
        />
      );
      break;
    case "multi_file_upload":
      control = (
        <UploadBox
          id={id}
          accept={FILE_ACCEPT}
          multiple
          compact
          noRemove={(f) => !!f?._saved}
          items={itemsOf(value)}
          busy={uploading}
          error={shownError}
          onPick={(files) => handlePick(files, true)}
          onRemove={(i) => onChange(itemsOf(value).filter((_, k) => k !== i))}
        />
      );
      break;
    case "signature":
      control = <SignaturePad onChange={onChange} />;
      break;
    case "description_text":
      control = (
        <div style={styles.descriptionBox}>
          <p style={styles.descriptionText}>{q.question}</p>
        </div>
      );
      break;
    case "section_heading":
      control = null;
      break;
    default:
      control = (
        <input
          id={id}
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
  }
  if (type === "section_heading") return null;

  const full =
    FULL_TYPES.has(type) || (/address/.test(lowerQ) && type !== "email");
  const short = isShortQ(q);
  const ageLocked = isAge && isAgeLockedFor(fieldKey, q);
    const lockAll = !!readonly;


  return (
    <div
      id={id}
      className={`ff-field${full ? " ff-full" : ""}${short ? " ff-short" : ""}`}
      style={styles.field}
    >
      {label}
      {ageLocked ? (
        <div>
          <input
            id={id}
            type="text"
            value={value || ""}
            readOnly
            style={{
              ...inputStyle,
              background: "#F9FAFB",
              color: "#6B7280",
              cursor: "not-allowed",
            }}
          />
          <div style={styles.hintGreen}>
            ✓ Auto-calculated from date of birth
          </div>
        </div>
      ) : lockAll ? (
        <div>
          <div
            style={{
              ...inputStyle,
              background: "#F9FAFB",
              color: "#6B7280",
              cursor: "not-allowed",
              minHeight: 44,
              display: "flex",
              alignItems: "center",
            }}
          >
            {isFileLike(value)
              ? asList(value).map((f, k) => (
                  <span key={k} style={{ marginRight: 8 }}>
                    📄 {f.name}
                  </span>
                ))
              : Array.isArray(value)
                ? value.join(", ")
                : String(value ?? "")}
          </div>
          <div style={styles.hintGreen}>
            🔒 Saved from a previous submission — can&apos;t be removed
          </div>
        </div>
      ) : (
        control
      )}
      {errEl}
    </div>
  );
}

/* ---------- SPECIAL BLOCKS ---------- */
export function HealthSection({
  baseIdx,
  ynQ,
  condQ,
  customQ,
  answers,
  errors,
  setAnswer,
  petCtxFor,
}) {
  // Gate is optional. If the form-builder disabled it, show the list straight away.
  const ynKey = ynQ ? petKey(ynQ._id, baseIdx) : null;
  const ynVal = ynQ ? answers[ynKey] : "";
  const showDetails = ynQ ? looksYes(ynVal, ynQ) : true; // ← key line

  const condKey = condQ ? petKey(condQ._id, baseIdx) : null;
  const condVal = condKey ? answers[condKey] : undefined;
  const condArr = Array.isArray(condVal) ? condVal : [];

  const condOpts = condQ ? normalizeOptions(condQ) : [];
  const showCustom =
    showDetails &&
    customQ &&
    condArr.some((v) => {
      const m = condOpts.find((o) => String(o.value) === String(v));
      const label = m ? m.label : String(v);
      return /^other$/i.test(label.trim());
    });

  const customKey = customQ ? petKey(customQ._id, baseIdx) : null;

  return (
    <>
      {ynQ && (
        <QuestionField
          q={ynQ}
          fieldKey={ynKey}
          value={ynVal}
          error={errors[ynKey]}
          onChange={(v) => setAnswer(ynQ, ynKey, v, baseIdx)}
          petCtx={petCtxFor(baseIdx)}
        />
      )}

      {showDetails && condQ && (
        <QuestionField
          q={condQ}
          fieldKey={condKey}
          value={condVal}
          error={errors[condKey]}
          onChange={(v) => setAnswer(condQ, condKey, v, baseIdx)}
          petCtx={petCtxFor(baseIdx)}
        />
      )}

      {showCustom && (
        <QuestionField
          q={customQ}
          fieldKey={customKey}
          value={answers[customKey]}
          error={errors[customKey]}
          onChange={(v) => setAnswer(customQ, customKey, v, baseIdx)}
          petCtx={petCtxFor(baseIdx)}
        />
      )}
    </>
  );
}

const medFieldKeyOf = (q) => {
  const t = qText(q);
  if (/name/.test(t)) return "name";
  if (/when/.test(t)) return "when";
  if (/dosage|how\s*much\s*to\s*give/.test(t)) return "dosage";
  if (/instruction/.test(t)) return "instructions";
  if (/photos?|images?|upload|document/.test(t)) return "photos";
  return `f_${q._id}`;
};

export function MedicationBlock({
  baseIdx,
  ynQ,
  fieldQs,
  answers,
  errors,
  setAnswer,
  setRawAnswer,
  petCtxFor,
  registerClear,
}) {
  const ynKey = ynQ ? petKey(ynQ._id, baseIdx) : null;
  const ynVal = ynQ ? answers[ynKey] : "";
  const showDetails = ynQ ? looksYes(ynVal, ynQ) : true;
  const itemsKey = `meds__${baseIdx}__items`;
  const items = Array.isArray(answers[itemsKey]) ? answers[itemsKey] : [];

  const blankItem = {
    name: "",
    when: [],
    dosage: "",
    instructions: "",
    photos: [],
  };

  useEffect(() => {
    if (showDetails && items.length === 0) {
      setRawAnswer(itemsKey, [blankItem]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetails]);

  const addItem = () => setRawAnswer(itemsKey, [...items, blankItem]);

  const removeItem = (i) => {
    if (items.length <= 1) return;
    setRawAnswer(
      itemsKey,
      items.filter((_, k) => k !== i),
    );
  };

  const updateItem = (i, field, val) =>
    setRawAnswer(
      itemsKey,
      items.map((m, k) => (k === i ? { ...m, [field]: val } : m)),
    );

  /* anything to clear? */
  const hasAnyData =
    (ynQ && !isEmpty(ynVal)) ||
    items.some(
      (m) =>
        !isEmpty(m?.name) ||
        !isEmpty(m?.dosage) ||
        !isEmpty(m?.instructions) ||
        (Array.isArray(m?.when) ? m.when.length > 0 : !isEmpty(m?.when)) ||
        (Array.isArray(m?.photos) && m.photos.length > 0),
    );

  const clearAll = () => {
    if (ynQ && ynKey) setAnswer(ynQ, ynKey, "", baseIdx);
    setRawAnswer(itemsKey, [blankItem]);
  };
    useEffect(() => {
      if (typeof registerClear === "function") registerClear(clearAll);
    });

  return (
    <>
      {ynQ && (
        <QuestionField
          q={ynQ}
          fieldKey={ynKey}
          value={ynVal}
          error={errors[ynKey]}
          onChange={(v) => setAnswer(ynQ, ynKey, v, baseIdx)}
          petCtx={petCtxFor(baseIdx)}
        />
      )}

      {!ynQ && showDetails && hasAnyData && (
        <div
          className="ff-field ff-full"
          style={{ ...styles.gateWithClear, justifyContent: "flex-end" }}
        >
          <button
            type="button"
            onClick={clearAll}
            style={styles.sectionClearBtnInline}
          >
            Clear section
          </button>
        </div>
      )}

      {showDetails &&
        items.map((m, i) => (
          <div key={i} className="ff-field ff-full" style={styles.medCard}>
            <div style={styles.medHeader}>
              <span style={styles.medTitle}>Medication {i + 1}</span>
              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(i)}
                  style={styles.removePetBtn}
                >
                  Remove
                </button>
              )}
            </div>
            <div className="ff-grid">
              {fieldQs.map((q) => {
                const f = medFieldKeyOf(q);
                const fk = `${itemsKey}-${i}-${f}`;
                return (
                  <QuestionField
                    key={q._id}
                    q={q}
                    fieldKey={fk}
                    value={m[f]}
                    onChange={(v) => updateItem(i, f, v)}
                    petCtx={petCtxFor(baseIdx)}
                  />
                );
              })}
            </div>
          </div>
        ))}

      {showDetails && (
        <div className="ff-field ff-full">
          <button type="button" onClick={addItem} style={styles.addOutlineBtn}>
            + Add Medication
          </button>
        </div>
      )}
    </>
  );
}

/* ---------- VACCINATION REPEATER ----------
   Same shape as MedicationBlock:
   - rows from the saved submission are marked `_existing` and locked
   - user can append new rows with the same field set
   - cannot remove an existing row
*/
export function VaccinationBlock({
  baseIdx,
  fieldQs,
  answers,
  errors,
  setRawAnswer,
  petCtxFor,
}) {
  const savedKey = `vacc__${baseIdx}__saved`;
  const itemsKey = `vacc__${baseIdx}__items`;
  const saved = Array.isArray(answers[savedKey]) ? answers[savedKey] : [];
  const items = Array.isArray(answers[itemsKey]) ? answers[itemsKey] : [];

  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? String(d).slice(0, 10) : dt.toLocaleDateString("en-GB");
  };

  useEffect(() => {
    if (saved.length === 0 && items.length === 0) setRawAnswer(itemsKey, [{}]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRow = () => setRawAnswer(itemsKey, [...items, {}]);
  const removeRow = (i) =>
    setRawAnswer(itemsKey, items.filter((_, k) => k !== i));
  const updateRow = (i, qid, val) =>
    setRawAnswer(
      itemsKey,
      items.map((r, k) => (k === i ? { ...r, [qid]: val } : r)),
    );

  return (
    <>
      {saved.map((r, i) => (
        <div key={`saved-${i}`} className="ff-field ff-full" style={styles.medCard}>
          <div style={styles.medHeader}>
            <span style={styles.medTitle}>
              {r.vaccine || "Vaccination"} · Record {i + 1}
            </span>
            <span style={{ fontSize: 12, color: "#059669", fontWeight: 600 }}>
              🔒 Saved
            </span>
          </div>
          <div style={{ fontSize: 14, color: "#374151" }}>
            Date given: <b>{fmtDate(r.date_given)}</b>
          </div>
          {Array.isArray(r.documents) && r.documents.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
              {r.documents.map((d, k) => (
                <a
                  key={k}
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 13, color: "#EA580C" }}
                >
                  📄 {d.name}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}

      {items.map((row, i) => (
        <div key={`new-${i}`} className="ff-field ff-full" style={styles.medCard}>
          <div style={styles.medHeader}>
            <span style={styles.medTitle}>New vaccination {i + 1}</span>
            {(saved.length > 0 || items.length > 1) && (
              <button type="button" onClick={() => removeRow(i)} style={styles.removePetBtn}>
                Remove
              </button>
            )}
          </div>
          <div className="ff-grid">
            {fieldQs.map((q) => (
              <QuestionField
                key={q._id}
                q={q}
                fieldKey={`${itemsKey}-${i}-${q._id}`}
                value={row[q._id]}
                error={errors[`${itemsKey}-${i}-${q._id}`]}
                onChange={(v) => updateRow(i, q._id, v)}
                petCtx={petCtxFor(baseIdx)}
              />
            ))}
          </div>
        </div>
      ))}

      <div className="ff-field ff-full">
        <button type="button" onClick={addRow} style={styles.addOutlineBtn}>
          + Add vaccination record
        </button>
      </div>
    </>
  );
}

const dietFieldKeyOf = (q) => {
  const t = qText(q);
  if (/what\s*to\s*feed/.test(t)) return "what";
  if (/when\s*to\s*feed/.test(t)) return "when";
  if (/how\s*much/.test(t)) return "how";
  if (/feeding\s*instructions/.test(t)) return "instructions";
  return `f_${q._id}`;
};

export function DietBlock({
  baseIdx,
  topQs,
  fieldQs,
  answers,
  errors,
  setAnswer,
  setRawAnswer,
  petCtxFor,
  registerClear,
}) {
  const itemsKey = `diet__${baseIdx}__items`;
  const items = Array.isArray(answers[itemsKey]) ? answers[itemsKey] : [];

  /* Meals Per Day drives the number of rows */
  const mealsQ = topQs.find(isMealsPerDayQ);
  const mealsKey = mealsQ ? petKey(mealsQ._id, baseIdx) : null;
  const mealsVal = mealsKey ? answers[mealsKey] : "";
  const wanted = Math.max(1, Math.min(6, parseInt(mealsVal, 10) || 1));

  const blank = { what: "", when: "", how: "", instructions: "" };

  /* Seed with one row on first render */
  useEffect(() => {
    if (!Array.isArray(answers[itemsKey]) || answers[itemsKey].length === 0) {
      setRawAnswer(itemsKey, [blank]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Auto-grow / auto-shrink to `wanted` */
  useEffect(() => {
    if (items.length === wanted) return;
    if (items.length < wanted) {
      setRawAnswer(itemsKey, [
        ...items,
        ...Array(wanted - items.length).fill(blank),
      ]);
    } else {
      setRawAnswer(itemsKey, items.slice(0, wanted));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted]);

  const updateItem = (i, field, val) =>
    setRawAnswer(
      itemsKey,
      items.map((m, k) => (k === i ? { ...m, [field]: val } : m)),
    );

  /* anything to clear? */
  const hasTopData = topQs.some(
    (q) => !isEmpty(answers[petKey(q._id, baseIdx)]),
  );
  const hasItemsData = items.some(
    (m) =>
      !isEmpty(m?.what) ||
      !isEmpty(m?.how) ||
      !isEmpty(m?.instructions) ||
      (Array.isArray(m?.when) ? m.when.length > 0 : !isEmpty(m?.when)),
  );
  const hasAnyData = hasTopData || hasItemsData;

  const clearAll = () => {
    topQs.forEach((q) => {
      const k = petKey(q._id, baseIdx);
      setAnswer(q, k, "", baseIdx);
    });
    setRawAnswer(itemsKey, [blank]);
  };
    useEffect(() => {
      if (typeof registerClear === "function") registerClear(clearAll);
    });

  return (
    <>
      {topQs.map((q) => {
        const k = petKey(q._id, baseIdx);
        return (
          <QuestionField
            key={q._id}
            q={q}
            fieldKey={k}
            value={answers[k]}
            error={errors[k]}
            onChange={(v) => setAnswer(q, k, v, baseIdx)}
            petCtx={petCtxFor(baseIdx)}
          />
        );
      })}

      {items.map((m, i) => (
        <div key={i} className="ff-field ff-full" style={styles.mealOptionCard}>
          <div style={styles.mealOptionHeader}>
            <span style={styles.mealOptionTitle}>Meal {i + 1}</span>
          </div>
          {fieldQs.map((q) => {
            const f = dietFieldKeyOf(q);
            const fk = `${itemsKey}-${i}-${f}`;
            return (
              <QuestionField
                key={q._id}
                q={q}
                fieldKey={fk}
                value={m[f]}
                onChange={(v) => updateItem(i, f, v)}
                petCtx={petCtxFor(baseIdx)}
              />
            );
          })}
        </div>
      ))}
    </>
  );
}

/* ---------- SHELL ---------- */
function Shell() {
  const {
    vendor,
    phase,
    step,
    stepKey,
    stepKeys,
    formLoading,
    formErr,
    submitted,
  } = useForm();
  const inForm = phase === "form" && !submitted;
  const ready = inForm && !formLoading && !formErr;
  const [logoError, setLogoError] = useState(false);
  useEffect(() => {
    setLogoError(false);
  }, [vendor?.logo]);
  
  return (
    <div className="ff-page">
      {phase !== "auth" && (
        <header className="ff-top">
          <div className="ff-top-inner">
            {vendor && (vendor.logo || vendor.name) && (
              <div className="ff-brand">
                {vendor.logo && !logoError ? (
                  <img
                    src={vendor.logo}
                    alt={vendor.name || "Logo"}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <div className="ff-brand-initial" aria-hidden="true">
                    {vendor.name && vendor.name.trim()
                      ? vendor.name.trim().charAt(0).toUpperCase()
                      : "P"}
                  </div>
                )}
                {vendor.name && <h2>{vendor.name}</h2>}
                {phase !== "auth" && !submitted && phase !== "success" && (
                  <span
                    className="ff-brand-form-name"
                    style={styles.brandFormName}
                  >
                    Customer Onboarding Form
                  </span>
                )}
              </div>
            )}
            {phase !== "success" && !submitted && (
              <Stepper
                keys={phase === "auth" ? ["customer", "pet"] : stepKeys}
                active={phase === "auth" ? -1 : step}
              />
            )}

            {ready && stepKey === "pet" && <PetTabs />}
          </div>
        </header>
      )}

      <main className="ff-main">
        {phase === "auth" && <OtpVerification />}

        {phase === "form" && formLoading && (
          <div>
            <div style={styles.skeletonLine} />
            <div style={styles.skeletonInput} />
            <div style={{ height: 16 }} />
            <div style={styles.skeletonLine} />
            <div style={styles.skeletonInput} />
          </div>
        )}
        {phase === "form" && !formLoading && formErr && (
          <p style={{ color: "#EF4444", fontSize: 14 }}>{formErr}</p>
        )}
        {ready && stepKey === "customer" && <CustomerDetails />}
        {ready && stepKey === "pet" && <PetDetails />}

        {phase === "form" && submitted && (
          <div style={styles.thankYou}>
            <h3 style={styles.thankYouTitle}>Thank you!</h3>
            <p style={styles.thankYouText}>
              Your form has been submitted. We'll review your information and be
              in touch shortly.
            </p>
          </div>
        )}
      </main>

      {phase === "success" && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalIcon}>✓</div>
            <h3 style={styles.modalTitle}>Verified</h3>
            <p style={styles.modalText}>Loading your form…</p>
            <div style={styles.modalSpinner} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Form() {
  const { vendorId = "", formId = "" } = useParams();
  return (
    <FormProvider vendorId={vendorId} formId={formId}>
      <Shell />
    </FormProvider>
  );
}

/* ---------- STYLES ---------- */
export const styles = {
  page: {
    minHeight: "100vh",
    background: "#F9FAFB",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "40px 16px",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    boxSizing: "border-box",
  },
  authHero: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    padding: "24px 0 28px",
  },
  authHeroLogo: {
    width: 120,
    height: 120,
    objectFit: "contain",
    marginBottom: 16,
    borderRadius: 16,
  },
  authHeroName: {
    margin: "0 0 6px",
    fontSize: 26,
    fontWeight: 700,
    color: "#111827",
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },
  authHeroWelcome: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
    lineHeight: 1.4,
    textAlign: "left",
    alignSelf: "flex-start",
    width: "100%",
  },
  headerFormNameBand: {
    margin: "8px -16px 0",
    padding: "10px 16px",
    background: "#F3F4F6",
    borderTop: "1px solid #E5E7EB",
    fontSize: 14,
    fontWeight: 600,
    color: "#394252",
    letterSpacing: "-0.01em",
  },
  pageFormNameBand: {
    background: "#F3F4F6",
    borderBottom: "1px solid #E5E7EB",
    padding: "10px 16px",
  },
  pageFormNameInner: {
    maxWidth: 800,
    margin: "0 auto",
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
    letterSpacing: "-0.01em",
  },
  card: {
    width: "100%",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
    border: "1px solid #E5E7EB",
    boxSizing: "border-box",
    transition: "max-width 0.25s",
  },
  stepper: {
    display: "flex",
    alignItems: "center",
    gap: 40,
    marginBottom: 32,
    borderBottom: "1px solid #E5E7EB",
    paddingBottom: 0,
  },
  stepItem: {
    position: "relative",
    paddingBottom: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "default",
  },
  stepLabel: { fontSize: 16, transition: "color 0.2s" },
  stepCheck: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  stepUnderline: {
    position: "absolute",
    bottom: "-1px",
    left: 0,
    right: 0,
    height: "3px",
    backgroundColor: "#2563EB",
    borderRadius: "2px 2px 0 0",
  },
  header: { marginBottom: 32 },
  title: {
    margin: "0 0 8px 0",
    fontSize: 28,
    fontWeight: 700,
    color: "#111827",
    lineHeight: 1.2,
  },
  subtitle: { margin: 0, fontSize: 15, color: "#6B7280", lineHeight: 1.6 },
  formName: {
    margin: "12px 0 0 0",
    fontSize: 13,
    color: "#9CA3AF",
    fontWeight: 500,
  },
  form: { display: "flex", flexDirection: "column", gap: 24 },
  field: { display: "flex", flexDirection: "column", minWidth: 0 },
  label: {
    fontSize: 15,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 8,
  },
  phoneRow: { display: "flex", alignItems: "stretch" },
  phonePrefix: {
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    fontSize: 16,
    fontWeight: 600,
    color: "#374151",
    background: "#F3F4F6",
    border: "1px solid #D1D5DB",
    borderRight: "none",
    borderRadius: "8px 0 0 8px",
  },
  bigInput: {
    padding: "14px 16px",
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: "0.02em",
    borderRadius: 8,
    border: "1px solid #D1D5DB",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    background: "#fff",
    color: "#111827",
    transition: "border-color 0.2s",
  },
  input: {
    padding: "12px 14px",
    fontSize: 15,
    borderRadius: 8,
    border: "1px solid #D1D5DB",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    background: "#fff",
    color: "#111827",
    fontFamily: "inherit",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  hintGreen: {
    fontSize: 12,
    color: "#059669",
    marginTop: 6,
    fontWeight: 500,
  },
  hintGrey: { fontSize: 12, color: "#6B7280", marginTop: 6 },
  successHint: {
    fontSize: 13,
    color: "#059669",
    marginTop: 10,
    fontWeight: 500,
  },
  resendHint: {
    fontSize: 14,
    color: "#6B7280",
    marginTop: 12,
    textAlign: "center",
  },
  resendLink: {
    background: "none",
    border: "none",
    color: "#EA580C",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline",
  },
  sendOtpBtn: {
    marginTop: 14,
    padding: "14px 18px",
    background: "#fff",
    color: "#EA580C",
    border: "1px solid #EA580C",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    width: "100%",
    transition: "background 0.2s",
  },
  otpWrap: { display: "flex", justifyContent: "center", gap: 12 },
  otpBox: {
    width: 56,
    height: 64,
    textAlign: "center",
    fontSize: 24,
    fontWeight: 700,
    borderRadius: 10,
    border: "1px solid #D1D5DB",
    outline: "none",
    background: "#fff",
    color: "#111827",
  },
  primaryBtn: {
    padding: "14px 24px",
    background: "#EA580C",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 4,
    transition: "background 0.2s",
  },
  secondaryBtn: {
    padding: "14px 20px",
    background: "#fff",
    color: "#374151",
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  navRow: { display: "flex", gap: 12, marginTop: 8, alignItems: "stretch" },
  errorBox: {
    background: "#FEF2F2",
    color: "#991B1B",
    padding: "14px 16px",
    borderRadius: 8,
    fontSize: 14,
    border: "1px solid #FECACA",
  },
  emptyText: {
    fontSize: 14,
    color: "#6B7280",
    textAlign: "center",
    margin: 0,
  },

  sectionHeading: {
    marginTop: 6,
    marginBottom: 2,
    paddingBottom: 10,
    borderBottom: "1px solid #E5E7EB",
  },
  sectionHeadingText: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
  },
  petSubHeading: { marginTop: 4, marginBottom: 2 },
  petSubHeadingText: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: "#6B7280",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },

  choiceWrap: { display: "flex", flexDirection: "column", gap: 8 },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    cursor: "pointer",
    background: "#fff",
  },
  radioRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  radioCol: { display: "flex", flexDirection: "column", gap: 10 },
  pillChoice: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    background: "#fff",
    cursor: "pointer",
    fontSize: 14,
    color: "#111827",
    transition: "border-color 0.15s, background 0.15s",
  },
  pillChoiceOn: { borderColor: "#EA580C", background: "#F0FDFA" },

  mealTypeBox: {
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: "16px 18px",
    background: "#fff",
  },
  mealTypeRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
  },
  mealTable: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    overflow: "hidden",
  },
  mealTableHead: {
    display: "flex",
    gap: 10,
    padding: "10px 12px",
    background: "#F9FAFB",
    borderBottom: "1px solid #E5E7EB",
  },
  mealTableRow: {
    display: "flex",
    gap: 10,
    padding: "10px 12px",
    alignItems: "center",
    borderBottom: "1px solid #F3F4F6",
  },
  mealTableCell: {
    flex: 1,
    fontSize: 13,
    color: "#4B5563",
    display: "flex",
    alignItems: "center",
  },
  mealTableHeadCell: {
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },

  petToolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  petCountText: { fontSize: 14, color: "#6B7280", fontWeight: 500 },
  addPetBtn: {
    padding: "10px 18px",
    background: "#EA580C",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  addPetDashed: {
    padding: "14px 18px",
    background: "#fff",
    color: "#EA580C",
    border: "1px dashed #EA580C",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
  },
  petCard: {
    border: " #E5E7EB",
    background: "#fff",
    borderRadius: 12,
    padding: "20px 20px 24px",
    scrollMarginTop: 16,
  },
  petCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  petCardTitleWrap: { display: "flex", alignItems: "center", gap: 10 },
  petAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#EA580C",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  petCardTitle: { margin: 0, fontSize: 16, fontWeight: 600, color: "#111827" },
  removePetBtn: {
    background: "none",
    border: "none",
    color: "#EF4444",
    fontSize: 13,
    fontWeight: 500,
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
  },
  uploadBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    minHeight: 180,
    padding: "24px 20px",
    border: "2px dashed #D1D5DB",
    borderRadius: 12,
    cursor: "pointer",
    textAlign: "center",
    boxSizing: "border-box",
    transition: "background 0.15s, border-color 0.15s",
  },
  uploadIcon: { marginBottom: 4, lineHeight: 0 },
  uploadTitle: { fontSize: 14, fontWeight: 500, color: "#374151" },
  uploadBrowse: {
    color: "#EA580C",
    textDecoration: "underline",
    fontWeight: 600,
    cursor: "pointer",
  },
  uploadSub: { fontSize: 12, color: "#9CA3AF" },
  uploadSpinner: {
    width: 28,
    height: 28,
    border: "3px solid #F3F4F6",
    borderTop: "3px solid #EA580C",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    marginBottom: 4,
  },
  fileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gap: 12,
    marginTop: 12,
  },
  fileItem: {
    position: "relative",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: 8,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
  },
  thumb: { width: "100%", height: 80, objectFit: "cover", borderRadius: 6 },
  fileIcon: {
    width: "100%",
    height: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 32,
    background: "#F3F4F6",
    borderRadius: 6,
  },
  fileItemName: {
    fontSize: 12,
    color: "#4B5563",
    wordBreak: "break-all",
    textAlign: "center",
  },
  fileOk: { fontSize: 11, color: "#059669", fontWeight: 600 },
  fileRemove: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 20,
    height: 20,
    lineHeight: "18px",
    borderRadius: "50%",
    border: "none",
    background: "rgba(0,0,0,0.5)",
    color: "#fff",
    fontSize: 14,
    cursor: "pointer",
    padding: 0,
  },
  locationBtn: {
    marginTop: 10,
    padding: "10px 14px",
    background: "#fff",
    color: "#EA580C",
    border: "1px solid #EA580C",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  descriptionBox: {
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "14px 16px",
  },
  descriptionText: {
    margin: 0,
    fontSize: 14,
    color: "#4B5563",
    lineHeight: 1.5,
  },
  signatureCanvas: {
    width: "100%",
    height: 150,
    border: "1px dashed #D1D5DB",
    borderRadius: 8,
    background: "#fff",
    touchAction: "none",
    cursor: "crosshair",
    display: "block",
  },
  clearBtn: {
    background: "none",
    border: "none",
    color: "#6B7280",
    fontSize: 13,
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
  },
  thankYou: { textAlign: "center", padding: "40px 0 20px" },
  thankYouIcon: { fontSize: 48, marginBottom: 12 },
  thankYouTitle: {
    fontSize: 22,
    fontWeight: 600,
    color: "#111827",
    margin: "0 0 8px",
  },
  thankYouText: { fontSize: 15, color: "#6B7280", margin: 0 },
  skeletonLine: {
    height: 12,
    borderRadius: 6,
    background: "#E5E7EB",
    width: "60%",
    margin: "0 auto 12px",
  },
  skeletonInput: {
    height: 44,
    borderRadius: 8,
    background: "#E5E7EB",
    marginBottom: 12,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    padding: 16,
  },
  modalCard: {
    background: "#fff",
    borderRadius: 12,
    padding: "32px 28px",
    maxWidth: 380,
    width: "100%",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  },
  modalIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "#ECFDF5",
    color: "#059669",
    fontSize: 34,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  modalTitle: {
    margin: "0 0 6px 0",
    fontSize: 20,
    fontWeight: 700,
    color: "#111827",
  },
  modalText: { margin: "0 0 18px 0", fontSize: 14, color: "#6B7280" },
  modalSpinner: {
    width: 24,
    height: 24,
    border: "3px solid #F3F4F6",
    borderTop: "3px solid #EA580C",
    borderRadius: "50%",
    margin: "0 auto",
    animation: "spin 0.8s linear infinite",
  },
  brand: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingBottom: 20,
    marginBottom: 24,
    borderBottom: "1px solid #E5E7EB",
  },
  brandFormName: {
    flexBasis: "100%",
    width: "100%",
    textAlign: "center",
    marginTop: 6,
    fontSize: 13,
    fontWeight: 600,
    color: "#6B7280",
    letterSpacing: "-0.01em",
  },
  authHeroFallback: {
    width: 120,
    height: 120,
    borderRadius: 16,
    background: "#EA580C",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 56,
    lineHeight: 1,
    marginBottom: 16,
    flexShrink: 0,
  },
  authCard: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 16,
    padding: "24px 20px 28px",
    boxShadow: "0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)",
  },
  brandLogo: {
    width: 48,
    height: 48,
    objectFit: "contain",
    borderRadius: 8,
    flexShrink: 0,
  },
  brandName: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    color: "#111827",
    lineHeight: 1.2,
    textAlign: "left",
  },
  addOutlineBtn: {
    padding: "12px 18px",
    background: "#fff",
    color: "#EA580C",
    border: "1px solid #EA580C",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  mealOptionCard: {
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: "20px",
    background: "#fff",
    marginTop: 12,
  },
  mealOptionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  mealOptionTitle: { fontSize: 15, fontWeight: 700, color: "#111827" },
  medCard: {
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: "20px",
    background: "#fff",
    marginTop: 12,
  },
  medHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  medTitle: { fontSize: 15, fontWeight: 700, color: "#111827" },
};
Object.assign(styles, {
  header: { marginBottom: 20 },
  title: {
    margin: "0 0 4px",
    fontSize: 22,
    fontWeight: 600,
    color: "#111827",
    lineHeight: 1.25,
  },
  subtitle: { margin: 0, fontSize: 14, color: "#6B7280", lineHeight: 1.5 },
  form: { display: "flex", flexDirection: "column", gap: 20 },
  label: { fontSize: 14, fontWeight: 500, color: "#111827", marginBottom: 6 },
  input: { ...styles.input, fontSize: 16 },
  bigInput: { ...styles.bigInput, fontSize: 16 },
  gateWithClear: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
  },
  gateQuestionWrap: { flex: 1, minWidth: 0 },
  sectionClearBtnInline: {
    background: "none",
    border: "none",
    color: "#EF4444",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: "4px 2px",
    textDecoration: "underline",
    whiteSpace: "nowrap",
  },
  sectionClearRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: 4,
    marginBottom: 4,
  },
  sectionClearBtn: {
    background: "none",
    border: "none",
    color: "#EF4444",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: "4px 2px",
    textDecoration: "underline",
  },
  sectionClearRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: 4,
    marginBottom: 4,
  },
  sectionClearBtn: {
    background: "none",
    border: "none",
    color: "#EF4444",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: "4px 2px",
    textDecoration: "underline",
  },
  createAccountBox: {
    marginTop: 24,
    paddingTop: 24,
    borderTop: "1px solid #7e7f81",
  },
  createAccountLabel: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    cursor: "pointer",
  },
  createAccountTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    lineHeight: 1.35,
  },
  createAccountDesc: {
    margin: "4px 0 0",
    fontSize: 12.5,
    lineHeight: 1.55,
    color: "#6B7280",
  },
  sectionHeadingText: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  petSubHeadingText: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
  },
  emptyText: { fontSize: 14, color: "#6B7280", margin: 0 },
  thankYou: { textAlign: "left", padding: "40px 0 20px" },
  primaryBtn: { ...styles.primaryBtn, borderRadius: 10 },
  secondaryBtn: { ...styles.secondaryBtn, borderRadius: 10 },
  otpWrap: { display: "flex", justifyContent: "flex-start", gap: 10 },
  otpBox: { ...styles.otpBox, width: 52, height: 56, fontSize: 22 },
  resendHint: { ...styles.resendHint, textAlign: "left" },
  mealOptionCard: { ...styles.mealOptionCard, padding: 16 },
  medCard: { ...styles.medCard, padding: 16 },
});


if (typeof document !== "undefined" && !document.getElementById("ff-css")) {
  const font = document.createElement("link");
  font.rel = "stylesheet";
  font.href =
    "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap";
  document.head.appendChild(font);

  const el = document.createElement("style");
  el.id = "ff-css";
  el.innerHTML = `
    :root {
      --ff-bg: #F7F8FA;
      --ff-surface: #FFFFFF;
      --ff-border: #E5E7EB;
      --ff-border-soft: #F3F4F6;
      --ff-text: #111827;
      --ff-text-2: #4B5563;
      --ff-text-3: #6B7280;
      --ff-text-4: #9CA3AF;
      --ff-primary: #EA580C;
      --ff-primary-soft: #F0FDFA;
      --ff-danger: #EF4444;
      --ff-danger-soft: #FEF2F2;
      --ff-radius: 12px;
      --ff-radius-lg: 16px;
      --ff-shadow-sm: 0 1px 2px rgba(16,24,40,.04);
      --ff-shadow-md: 0 2px 6px rgba(16,24,40,.06), 0 1px 2px rgba(16,24,40,.04);
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .ff-page * { box-sizing: border-box; }
    .ff-page {
      min-height: 100vh;
      background: var(--ff-bg);
      color: var(--ff-text);
      font-family: 'Poppins', system-ui, -apple-system, sans-serif;
      -webkit-font-smoothing: antialiased;
      text-align: left;
    }
    .ff-page button,
    .ff-page input,
    .ff-page select,
    .ff-page textarea { font-family: inherit; }
    .ff-top {
      position: sticky; top: 0; z-index: 50;
      background: rgba(255,255,255,.92);
      backdrop-filter: saturate(180%) blur(8px);
      -webkit-backdrop-filter: saturate(180%) blur(8px);
      border-bottom: 1px solid var(--ff-border);
    }
    .ff-top-inner { max-width: 800px; margin: 0 auto; padding: 12px 16px 0; }
        .ff-brand {
      display: flex; align-items: center; gap: 10px;
      flex-wrap: wrap;
      margin-bottom: 12px;
      position: relative;
    }
    .ff-brand img {
      width: 30px; height: 30px; object-fit: contain;
      border-radius: 8px; background: #fff; flex-shrink: 0;
    }
          .ff-brand-initial {
      width: 30px; height: 30px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 8px;
      background: var(--ff-primary);
      color: #fff;
      font-size: 15px; font-weight: 700;
      flex-shrink: 0;
    }
         .ff-brand h2 {
      margin: 0; font-size: 15px; font-weight: 600; color: var(--ff-text);
      letter-spacing: -0.01em;
      word-break: break-word;
    }
    .ff-steps {
      list-style: none; margin: 0; padding: 0 0 12px;
      display: flex; align-items: center;
      overflow-x: auto; scrollbar-width: none;
    }
    .ff-steps::-webkit-scrollbar { display: none; }
    .ff-step {
      display: flex; align-items: center; gap: 8px;
      color: var(--ff-text-4); font-size: 12px; font-weight: 500;
      white-space: nowrap; flex-shrink: 0;
    }
    .ff-step:not(:last-child) { flex: 1; min-width: 36px; }
    .ff-step:not(:last-child)::after {
      content: ""; flex: 1; height: 2px; margin: 0 8px;
      background: var(--ff-border); border-radius: 2px;
      transition: background .25s ease;
    }
    .ff-step.is-done:not(:last-child)::after { background: var(--ff-primary); }
    .ff-step-dot {
      width: 22px; height: 22px; border-radius: 50%;
      display: grid; place-items: center;
      font-size: 11px; font-weight: 600;
      background: var(--ff-border-soft); color: var(--ff-text-4);
      flex-shrink: 0;
      transition: background .2s ease, color .2s ease, box-shadow .2s ease;
    }
    .ff-step.is-active { color: var(--ff-text); font-weight: 600; }
    .ff-step.is-active .ff-step-dot,
    .ff-step.is-done  .ff-step-dot { background: var(--ff-primary); color: #fff; }
    .ff-step.is-done { color: var(--ff-text-2); }
    @media (max-width: 480px) {
      .ff-step-label { display: none; }
      .ff-step.is-active .ff-step-label { display: inline; }
    }
    @media (min-width: 640px) {
      .ff-step { font-size: 13px; }
      .ff-step-dot { width: 24px; height: 24px; font-size: 12px; }
      .ff-step:not(:last-child)::after { margin: 0 12px; }
    }
    .ff-pets {
      border-top: 1px solid var(--ff-border-soft);
      padding: 12px 0 12px;
    }
    .ff-pets-head {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 12px;
    }
    .ff-pets-title {
      font-size: 11px; font-weight: 600; color: var(--ff-text-3);
      text-transform: uppercase; letter-spacing: .08em;
    }
    .ff-add-pet {
      display: inline-flex; align-items: center; gap: 4px;
      background: var(--ff-primary); color: #fff;
      border: 0; border-radius: 999px;
      padding: 7px 14px;
      font-size: 12px; font-weight: 600;
      cursor: pointer;
      transition: opacity .15s, transform .12s;
    }
    .ff-add-pet:disabled { opacity: .5; cursor: not-allowed; }
    .ff-add-pet:active:not(:disabled) { transform: translateY(1px); }
    .ff-pet-scroll {
      display: flex; gap: 14px;
      overflow-x: auto;
      padding: 4px 2px 6px;
      scrollbar-width: none;
      scroll-snap-type: x proximity;
      -webkit-overflow-scrolling: touch;
    }
    .ff-pet-scroll::-webkit-scrollbar { display: none; }
    .ff-pet-tab {
      background: none; border: 0; padding: 0;
      width: 60px; flex-shrink: 0;
      display: flex; flex-direction: column; align-items: center; gap: 6px;
      cursor: pointer;
      scroll-snap-align: start;
      transition: transform .15s ease;
    }
    .ff-pet-tab:active { transform: scale(.96); }
    .ff-pet-avatar {
      width: 44px; height: 44px; border-radius: 50%;
      display: grid; place-items: center;
      font-size: 15px; font-weight: 600;
      background: var(--ff-border-soft); color: var(--ff-text-3);
      transition: background .2s ease, color .2s ease, box-shadow .2s ease;
    }
    .ff-pet-name {
      font-size: 11px; color: var(--ff-text-3);
      max-width: 60px;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .ff-pet-tab.is-on .ff-pet-avatar {
      background: var(--ff-primary); color: #fff;
      box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ff-primary);
    }
    .ff-pet-tab.is-on .ff-pet-name { color: var(--ff-text); font-weight: 600; }
    .ff-pet-tab.has-error .ff-pet-avatar {
      background: var(--ff-danger-soft); color: #B91C1C;
    }
    .ff-pet-tab.has-error.is-on .ff-pet-avatar {
      background: var(--ff-danger); color: #fff;
      box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ff-danger);
    }
    .ff-main {
      max-width: 800px;
      margin: 0 auto;
      padding: 20px 16px 128px;
      text-align: left;
    }
    .ff-panel {
      background: var(--ff-surface);
      border: 1px solid var(--ff-border);
      border-radius: var(--ff-radius-lg);
      padding: 20px 16px;
      box-shadow: var(--ff-shadow-sm);
    }
    .ff-panel-head {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 16px;
    }
    .ff-panel-title {
      margin: 0;
      font-size: 16px; font-weight: 600; color: var(--ff-text);
      letter-spacing: -0.01em;
    }
    .ff-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .ff-field { min-width: 0; grid-column: 1 / -1; }
    .ff-short { grid-column: span 1; }
    .ff-full  { grid-column: 1 / -1; }
    .ff-err { font-size: 12px; color: var(--ff-danger); margin-top: 6px; }
    .ff-consent-list {
      margin: 0 0 18px;
      padding-left: 18px;
      font-size: 14px; line-height: 1.7; color: var(--ff-text-2);
    }
    .ff-consent-list li + li { margin-top: 6px; }
    .ff-consent-check {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 14px 16px;
      border: 1px solid var(--ff-border);
      border-radius: var(--ff-radius);
      font-size: 14px; color: var(--ff-text);
      background: var(--ff-surface);
      cursor: pointer;
      transition: border-color .15s ease, background .15s ease;
    }
    .ff-consent-check:hover { background: #FAFAFA; }
    .ff-consent-check.has-error {
      border-color: var(--ff-danger);
      background: var(--ff-danger-soft);
    }
    .ff-nav {
      position: fixed; left: 0; right: 0; bottom: 0;
      z-index: 40;
      background: rgba(255,255,255,.96);
      backdrop-filter: saturate(180%) blur(8px);
      -webkit-backdrop-filter: saturate(180%) blur(8px);
      border-top: 1px solid var(--ff-border);
      padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    }
    .ff-nav-inner {
      max-width: 800px;
      margin: 0 auto;
      display: flex; gap: 10px;
    }
    .ff-nav-inner button { flex: 1; }
    .ff-page input:focus,
    .ff-page select:focus,
    .ff-page textarea:focus {
      border-color: var(--ff-primary) !important;
      box-shadow: 0 0 0 3px rgba(13,124,107,.12);
      outline: none;
    }
    .ff-page button { transition: opacity .15s, transform .12s; }
    .ff-page button:hover:not(:disabled)  { opacity: .92; }
    .ff-page button:active:not(:disabled) { transform: translateY(1px); }
    @media (max-width: 767px) {
      .ff-page input,
      .ff-page select,
      .ff-page textarea { font-size: 16px; }
    }
    
    @media (min-width: 768px) {
      .ff-top-inner { padding: 16px 24px 0; max-width: 880px; }
      .ff-brand { margin-bottom: 14px; }
      .ff-brand img { width: 34px; height: 34px; }
      .ff-brand h2 { font-size: 16px; }
      .ff-main {
        padding: 28px 24px 148px;
        max-width: 880px;
      }
      .ff-panel {
        padding: 28px 28px 32px;
        border-radius: 20px;
        box-shadow: var(--ff-shadow-md);
      }
      .ff-panel-title { font-size: 17px; }
      .ff-grid { gap: 20px 24px; }
      .ff-field { grid-column: auto; }
      .ff-full  { grid-column: 1 / -1; }
      .ff-short { grid-column: span 1; }
    }
    @media (min-width: 1024px) {
      .ff-top-inner { max-width: 920px; }
      .ff-main { max-width: 920px; padding-left: 32px; padding-right: 32px; }
    }
  `;
  document.head.appendChild(el);
}